import api from './client';
import { guest as GuestAPI } from './guest';
import { storage } from '../utils/storage';
import { readGuestToken } from './guestToken.js';

const V2 = '/v2/requirements';

// Chat turns can take ~10–30s on a small local LLM. The default axios timeout
// (10s) fires too early and surfaces as REQS.INTERACTION_FAILED even though
// the server completed cleanly.
const AI_TIMEOUT_MS = 60000;

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

const isGuestMode = () => {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname.startsWith('/guest/')) return true;
  // The emailed invitation link lands here with no account at all. Opening it
  // needs a guest session (the route is authorizeAny); without this the open
  // call went out bare, 401'd, and the client bounced the invitee to
  // /login?reason=session_expired with the invitation URL lost.
  if (window.location.pathname.startsWith('/invite/')) {
    let accessToken = '';
    try { accessToken = storage.get(storage.KEYS.ACCESS_TOKEN) || ''; } catch (_) {}
    if (!accessToken) return true;
  }
  const guestToken = readGuestToken();
  // Tolerate accessToken not being present (e.g. on fresh load); we only
  // treat the session as guest if we have a guest token AND no access token.
  //
  // Read through `storage`, which is where auth WRITES it. This read went
  // straight to localStorage while the auth layer stores the token in
  // sessionStorage, so for every signed-in user the access token looked
  // absent: anyone who had ever browsed as a guest first still had a
  // guestToken, `isGuestMode()` stayed true after they logged in, and the
  // requirements API sent the GUEST token instead of theirs.
  //
  // Their interview was then created and owned by `guest_…` rather than by
  // their account. Seen end to end: signed in as a real customer, ran a
  // 25-question interview, and the project came back
  // `ownerId: guest_85327324-…`. The account cannot own it, ownership checks
  // would refuse them, and clearing site data orphans work they paid for.
  let accessToken = '';
  try { accessToken = storage.get(storage.KEYS.ACCESS_TOKEN) || ''; } catch (_) {}
  return Boolean(guestToken && !accessToken);
};

// When the page is a guest page, mint/reuse a guest token and attach it as
// an explicit Authorization header. The shared axios instance only injects
// the regular ACCESS_TOKEN by default, which doesn't exist for guests.
const withGuestAuth = async (cfg = {}) => {
  if (!isGuestMode()) return cfg;
  const token = await GuestAPI.ensureSession();
  return {
    ...cfg,
    headers: { ...(cfg.headers || {}), Authorization: `Bearer ${token}` }
  };
};

const guestRetry = async (fn) => {
  try {
    return await fn();
  } catch (err) {
    if (err?.response?.status !== 401 || !isGuestMode()) throw err;
    // ⚠️ This used to be `GuestAPI.clearToken()` — the second copy of the same
    // destructive answer to a 401. A guest's work is owned by their token's
    // `sub`, so minting a new session makes the visitor a different person who
    // owns none of it; a gateway restart was enough to lose a finished
    // interview, and the only symptom was a bare 404 on their own report.
    //
    // One implementation now, in GuestAPI, so the two paths cannot drift into
    // disagreeing about when it is safe to replace an identity.
    await GuestAPI.recoverFrom401();
    return fn();
  }
};

// Transient-failure retry for chat turns, so a blip never reads as a broken
// conversation. Two cases, deliberately different:
//
//   429 — the limiter is closed. Wait what Retry-After says (capped at 10s) and
//         try ONCE; a longer wait is not worth holding the spinner for, so it
//         surfaces at once WITH the number and the composer counts it down.
//   502/503/504 — our side restarting or the model layer refusing fast (outage
//         mode). Nothing was processed, so retrying is safe. Two more tries
//         with a growing pause (1.5s, then 4s — or the server's own hint), then
//         surface, so a real outage ends in finite time with a clear message.
//
// A timeout or a dropped connection is NOT retried here: the server may have
// taken the turn, and the send path's questionCode guard (V2.QUESTION_MOVED)
// is what makes a manual re-send safe — an automatic one would not be.
const MAX_AUTO_WAIT_MS = 10_000;
const retryAfterMsOf = (err) => {
  const h = err?.response?.headers;
  const raw = Number(h?.get ? h.get('retry-after') : h?.['retry-after']);
  return Number.isFinite(raw) && raw > 0 ? raw * 1000 : null;
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const rateLimitRetry = async (fn) => {
  const backoffs = [1500, 4000];
  let lastErr;
  for (let attempt = 0; attempt <= backoffs.length; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Offline is a decision, not a hiccup: retrying only adds load.
      if (/MOKSY_OFFLINE|\.MAINTENANCE$/.test(String(err?.response?.data?.code || err?.response?.data?.error?.code || ''))) throw err;
      const hinted = retryAfterMsOf(err);
      const unavailable = status === 502 || status === 503 || status === 504;
      if (status === 429) {
        if (attempt > 0 || (hinted != null && hinted > MAX_AUTO_WAIT_MS)) throw err;
        // eslint-disable-next-line no-await-in-loop
        await sleep(hinted != null ? Math.max(500, hinted) : 1500);
        continue;
      }
      if (!unavailable || attempt >= backoffs.length) throw err;
      if (hinted != null && hinted > MAX_AUTO_WAIT_MS) throw err; // long outage: tell them, don't spin
      // eslint-disable-next-line no-await-in-loop
      await sleep(hinted != null ? Math.max(500, hinted) : backoffs[attempt]);
    }
  }
  throw lastErr;
};
export { rateLimitRetry as __rateLimitRetryForTest };

// Retry for the guest → member claim specifically.
//
// The claim is the single least forgiving call in the product: it runs right
// after someone signs up, on work they spent an interview producing, and a
// failure that is not recovered reads as "the thing I just made is gone".
//
// The server moves dependants before the project itself, so a failed claim
// leaves the project still owned by the guest and the whole operation safely
// repeatable — which is what makes retrying here correct rather than reckless.
// Only transient conditions are retried; an ownership mismatch is a decision,
// not a blip, and is surfaced immediately.
const RETRYABLE_CLAIM_CODES = new Set(['V2.CLAIM_TRANSFER_FAILED']);
const isRetryableClaimError = (err) => {
  const status = err?.response?.status;
  // No response at all = network/offline, worth another go.
  if (!err?.response) return true;
  if (status >= 500) return true;
  return RETRYABLE_CLAIM_CODES.has(err?.response?.data?.error?.code);
};

const claimRetry = async (fn, attempts = 3) => {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isRetryableClaimError(err) || i === attempts - 1) throw err;
      // Backoff: 600ms, 1800ms. Short enough to finish inside the post-signup
      // moment while the user is still watching.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 600 * (3 ** i)));
    }
  }
  throw lastErr;
};

class RequirementsV2API {
  static async info() {
    const res = await api.get(`${V2}/info`);
    return unwrap(res);
  }

  static async catalogSummary() {
    const res = await guestRetry(async () => api.get(`${V2}/catalog`, await withGuestAuth()));
    return unwrap(res);
  }

  static async start(projectId, orgId = null, idea = '', holderLabel = '') {
    // Starting a SEEDED project runs LLM relevance-detection server-side (10–30s+),
    // so give it the long AI timeout like chat/finalise — otherwise the browser
    // aborts before the gateway/backend finish and the interview 500s even though
    // the project was created.
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects`, { projectId, orgId, idea, ...(holderLabel ? { holderLabel } : {}) }, await withGuestAuth({ timeout: AI_TIMEOUT_MS }))
    );
    return unwrap(res);
  }

  // Restart wipes chatHistory + answers for an in-flight project.
  // Distinct from start() (idempotent resume). Finalised projects
  // can't be restarted — backend returns 409.
  static async restart(projectId) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/restart`, {}, await withGuestAuth())
    );
    return unwrap(res);
  }

  // Claim — transfer a guest-owned project to the currently-
  // authenticated user. Caller passes the OLD guest token; the
  // gateway verifies its signature and confirms the project's
  // current ownerId matches the guest token's sub before swapping
  // ownership. Idempotent: claiming a project you already own is a
  // no-op success.
  // Retried on transient failure — see claimRetry. Throws on a definitive
  // outcome (e.g. ownership mismatch) so the caller can say something true
  // instead of promising a link-up that is never going to happen.
  static async claim(projectId, guestToken) {
    const res = await claimRetry(() => api.post(
      `${V2}/projects/${projectId}/claim`,
      { guestToken }
    ));
    return unwrap(res);
  }

  static async submitAnswer(projectId, code, value) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/answer`, { code, value }, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async readAnswers(projectId, { view = 'user' } = {}) {
    const res = await guestRetry(async () =>
      api.get(`${V2}/projects/${projectId}/answers`, await withGuestAuth({ params: { view }, timeout: 30000 }))
    );
    return unwrap(res);
  }

  // Confirm or correct what Moksy decided about the project. Both go through
  // guestRetry and withGuestAuth like the other project verbs: a guest owns
  // their project, and is the one most likely to have been handed a wrong
  // guess — they gave the least input for it to work from.
  //
  // Neither regenerates. The caller decides whether the correction is worth
  // paying to re-run, which is why these return the fresh interview state
  // rather than kicking off work.
  /**
   * Confirm what Moksy read from the idea, or correct it.
   *
   * A correction APPENDS to the idea server-side rather than replacing it —
   * everything downstream reads project.idea, so overwriting would discard
   * what they originally wrote.
   */
  // `recall`: 'same' carries the earlier confirmed discovery over and confirms;
  // 'different' records the answer and leaves the read-back open.
  // The generation-time card (profile plan M2).
  static async generationCard(projectId) {
    const res = await guestRetry(async () => api.get(`${V2}/projects/${projectId}/generation-card`, await withGuestAuth()));
    return unwrap(res);
  }
  static async answerGenerationCard(projectId, name, value) {
    const res = await guestRetry(async () => api.post(`${V2}/projects/${projectId}/generation-card`, { name, value }, await withGuestAuth()));
    return unwrap(res);
  }
  // The read-back's corrections above the problem: the name and the holder.
  static async setProjectName(projectId, name) {
    const res = await guestRetry(async () => api.post(`${V2}/projects/${projectId}/name`, { name }, await withGuestAuth()));
    return unwrap(res);
  }
  static async setProjectHolder(projectId, { orgId = null, label = '' } = {}) {
    const res = await guestRetry(async () => api.post(`${V2}/projects/${projectId}/holder`, { orgId, label }, await withGuestAuth()));
    return unwrap(res);
  }
  static async confirmIdeaReadBack(projectId, correction = '', recall = null, extra = null) {
    // `extra` carries the second confirmation: `{ stage: 'solution' }`, with
    // an optional hand-edited `parts` list (entity model §5 row 5).
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/idea-readback`, { correction, ...(recall ? { recall } : {}), ...(extra && typeof extra === 'object' ? extra : {}) }, await withGuestAuth())
    );
    return unwrap(res);
  }
  /**
   * 🙅 "That is not a challenge" — the second way past the discovery gate,
   * which its own refusal names and which nothing implemented until
   * 2026-09-08. Pass `dismissed: false` to put one back.
   */
  static async setChallengeDismissed(projectId, challengeId, { reason = '', dismissed = true } = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/challenges/${encodeURIComponent(challengeId)}/dismissed`, { reason, dismissed }, await withGuestAuth())
    );
    return unwrap(res);
  }
  /** 🔒 Take the personal details out of the brief. The server recomputes the
   *  redaction from the stored text, so this sends nothing. */
  static async redactIdea(projectId) {
    const res = await guestRetry(async () => api.post(`${V2}/projects/${projectId}/redact`, {}, await withGuestAuth()));
    return unwrap(res);
  }
  /** 🚩 Somebody says something here is wrong. Open to anyone who can reach
   *  the material — including a reader who only ever had the link. */
  static async reportAbuse({ subjectKind, subjectId, reason, detail = '' }) {
    const res = await guestRetry(async () => api.post(`${V2}/reports/abuse`, { subjectKind, subjectId, reason, detail }, await withGuestAuth()));
    return unwrap(res);
  }
  /** 📋 What is held about the caller, by class, with counts — works with a
   *  guest token, because the panel that asks lives on the guest Briefing. */
  static async myDataClasses() {
    const res = await guestRetry(async () => api.get(`${V2}/me/data-classes`, await withGuestAuth()));
    return res?.data?.data ?? res?.data ?? { classes: [] };
  }

  /** 🔐 Everything held about the caller — works with a guest token. */
  static async exportMyData() {
    const res = await guestRetry(async () => api.get(`${V2}/me/data-export`, await withGuestAuth()));
    return res?.data?.data ?? res?.data;
  }

  /** 🗑 Ask for it to be deleted. The server requires the caller's own id back
   *  as a confirmation, which is the deliberate double-check. */
  static async deleteMyData() {
    const auth = await withGuestAuth();
    const token = String(auth?.headers?.Authorization || '').replace(/^Bearer\s+/i, '');
    let confirm = '';
    try { confirm = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub || ''; } catch { confirm = ''; }
    const res = await guestRetry(async () => api.post(`${V2}/me/deletion-request`, { confirm, reason: 'requested from the Briefing' }, await withGuestAuth()));
    return unwrap(res);
  }

  // The customer moves the market (uk · eu · remote · bd) or the staffing
  // policy (cheapest · senior) and the plan re-costs.
  static async setTeamPlanOptions(projectId, { market = null, policy = null, vector = null } = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/team-plan`, { ...(market ? { market } : {}), ...(policy ? { policy } : {}), ...(vector ? { vector } : {}) }, await withGuestAuth())
    );
    return unwrap(res);
  }
  // 🧭 The project as the centre, in one read (entity model §5).
  static async projectModel(projectId) {
    const res = await guestRetry(async () => api.get(`${V2}/projects/${projectId}/model`, await withGuestAuth()));
    return unwrap(res);
  }
  // 📚 Many reports on one project: the index, and a second type.
  static async listReports(projectId) {
    const res = await guestRetry(async () => api.get(`${V2}/projects/${projectId}/reports`, await withGuestAuth()));
    const data = unwrap(res);
    return { reports: Array.isArray(data?.reports) ? data.reports : [], active: data?.active || null };
  }
  static async setReportType(projectId, reportTypeCode) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/report-type`, { reportTypeCode }, await withGuestAuth())
    );
    return unwrap(res);
  }
  // Pass '' to mean "none of these fit" — the category is cleared and the
  // template chain resolves at Master instead of keeping a rejected guess.
  static async setCategory(projectId, categoryCode) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/category`, { categoryCode }, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async overrideAnswer(projectId, code, value, reason = '') {
    const res = await guestRetry(async () =>
      api.patch(`${V2}/projects/${projectId}/answers/${code}`, { value, reason }, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async reviewAnswer(projectId, code, { verdict, correctedValue, notes = '' }) {
    const res = await guestRetry(async () =>
      api.post(
        `${V2}/projects/${projectId}/answers/${code}/review`,
        { verdict, correctedValue, notes },
        await withGuestAuth()
      )
    );
    return unwrap(res);
  }

  /**
   * Stop a generation that is under way.
   *
   * Not on the long AI timeout that finalise uses: stopping is a flag write and
   * a job cancel, and it must answer immediately. Somebody pressing stop has
   * already waited longer than they wanted to.
   */
  static async stopGeneration(projectId) {
    const res = await api.post(`${V2}/projects/${projectId}/generation/stop`, {}, await withGuestAuth());
    return unwrap(res);
  }

  static async finalise(projectId, { force = false, full = false } = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/finalise`, { force, full }, await withGuestAuth({ timeout: AI_TIMEOUT_MS }))
    );
    return unwrap(res);
  }

  // `questionCode` names the question the text answers (a tapped chip, a
  // typed reply). The server refuses a turn whose question has moved on
  // (V2.QUESTION_MOVED) instead of filing it under the next one.
  static async chat(projectId, message, questionCode = null) {
    // Two-layer retry: 401 → refresh guest token (existing) and 429 →
    // wait once + retry (new). The 429 retry is critical for the
    // interview UX — the LLM rate limiter can briefly trip when the
    // user types two messages quickly or right at the boundary of the
    // limiter window. Forcing the user to manually retry feels like
    // the conversation broke.
    const res = await guestRetry(async () =>
      rateLimitRetry(async () =>
        api.post(
          `${V2}/projects/${projectId}/chat`,
          { message, ...(questionCode ? { questionCode } : {}) },
          await withGuestAuth({ timeout: AI_TIMEOUT_MS })
        )
      )
    );
    return unwrap(res);
  }

  // In-memory outage state, no provider call — safe to poll from every open tab.
  static async llmStatus() {
    const res = await api.get(`${V2}/llm/status`);
    return unwrap(res);
  }

  static async llmHealth() {
    const res = await api.get(`${V2}/llm/health`);
    return unwrap(res);
  }

  // ── Reports ──────────────────────────────────────────────────────────
  static async getReport(projectId) {
    // Polled during generation (busy service, growing partial doc) — the default
    // 10s axios timeout is too tight, surfacing as "timeout of 10000ms exceeded".
    const res = await guestRetry(async () =>
      api.get(`${V2}/projects/${projectId}/report`, await withGuestAuth({ timeout: 30000 }))
    );
    return unwrap(res);
  }
  // Public "share for review" report. NOT under /v2/requirements — it hits the
  // gateway's /p/:slug/report handler, which validates the share token in `?t=`
  // (that token IS the authorization) and returns the full pillar report. No
  // session/guest auth is attached deliberately; a reviewer need not be a user.
  // One id per browser so a landing and its stay ping are the same visitor
  // (earning plan share_report); the gateway hashes it, nothing else is sent.
  static visitorId() {
    try { const k = 'moksy_visitor'; let v = localStorage.getItem(k); if (!v) { v = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`); localStorage.setItem(k, v); } return v; } catch { return null; }
  }
  static async sharedReportVisit(slug, token, stage = 'stay', interactions = 0) {
    return (await api.post(`/p/${encodeURIComponent(slug)}/visit`, { stage, interactions, visitorId: RequirementsV2API.visitorId(), t: token }, { params: token ? { t: token } : {} })).data.data;
  }
  static async getSharedReport(slug, token) {
    const res = await api.get(`/p/${encodeURIComponent(slug)}/report`, {
      params: token ? { t: token } : {},
      headers: { 'x-moksy-visitor': RequirementsV2API.visitorId() || '' },
      timeout: 30000
    });
    return unwrap(res);
  }

  // Download the report as a PDF (Blob) via the downloads service — which now
  // renders the v2 base-template composite report. Guest-aware.
  static async downloadReportPdf(projectId) {
    const guest = isGuestMode();
    // `fresh=1` bypasses the storage-vault cache so the PDF always reflects the
    // current report (the vault is write-once and would serve a stale document).
    const path = guest ? `/guest/downloads/${projectId}/pdf?fresh=1` : `/downloads/${projectId}/pdf?fresh=1`;
    const res = await api.get(path, await withGuestAuth({ responseType: 'blob', timeout: 120000 }));
    return res.data;
  }

  /**
   * Blueprint interchange exports (plan §7): 'report.json' (canonical),
   * 'report.md' (Markdown) or 'bundle' (AI-readable). Members only — a guest's
   * export is the PDF teaser. Returns a Blob with the server's content type.
   */
  static async downloadReportFile(projectId, file = 'report.md') {
    const res = await api.get(`${V2}/projects/${projectId}/${file}`, { responseType: 'blob', timeout: 60000 });
    return res.data;
  }

  // ── Expert review marketplace ────────────────────────────────────────
  // Owner side. Backend accepts: { scope, sections, track, requiredLevel, priceMox }.
  static async requestReview(projectId, payload = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/reviews`, payload, await withGuestAuth())
    );
    return unwrap(res);
  }

  // ── The job pool: real job posts the team runs through Moksy ─────────
  static async sourcingAccess() { return unwrap(await api.get(`${V2}/sourcing/access`)); }
  static async listSourcedJobs(params = {}) { return unwrap(await api.get(`${V2}/sourcing/jobs`, { params })); }
  static async getSourcedJob(id) { return unwrap(await api.get(`${V2}/sourcing/jobs/${encodeURIComponent(id)}`)); }
  static async addSourcedJob(payload) { return unwrap(await api.post(`${V2}/sourcing/jobs`, payload)); }
  static async sourcedJobStartPayload(id) { return unwrap(await api.get(`${V2}/sourcing/jobs/${encodeURIComponent(id)}/start-payload`)); }
  static async sourcingCoverage() { return unwrap(await api.get(`${V2}/sourcing/coverage`)); }
  static async sourcingImportLatest() { return unwrap(await api.get(`${V2}/sourcing/imports`)); }
  static async startSourcingImport(payload = {}) { return unwrap(await api.post(`${V2}/sourcing/imports`, payload)); }
  static async sourcingImportRun(runId) { return unwrap(await api.get(`${V2}/sourcing/imports/${encodeURIComponent(runId)}`)); }
  static async stopSourcingImport(runId) { return unwrap(await api.post(`${V2}/sourcing/imports/${encodeURIComponent(runId)}/stop`)); }
  // Runs over the pool, batches and the issue log (real-jobs brief §10, §12).
  static async poolSummary() { return unwrap(await api.get(`${V2}/sourcing/pool/summary`)); }
  static async poolBatches() { return unwrap(await api.get(`${V2}/sourcing/batches`)); }
  static async advancePoolBatch(batchId, to, note = '') { return unwrap(await api.post(`${V2}/sourcing/batches/${encodeURIComponent(batchId)}/advance`, { to, note })); }
  static async jobPoolRuns(id) { return unwrap(await api.get(`${V2}/sourcing/jobs/${encodeURIComponent(id)}/runs`)); }
  static async startPoolRun(id, payload = {}) { return unwrap(await api.post(`${V2}/sourcing/jobs/${encodeURIComponent(id)}/runs`, payload)); }
  static async poolRun(runId) { return unwrap(await api.get(`${V2}/sourcing/runs/${encodeURIComponent(runId)}`)); }
  static async poolRunStep(runId, payload) { return unwrap(await api.post(`${V2}/sourcing/runs/${encodeURIComponent(runId)}/steps`, payload)); }
  static async poolRunVerdicts(runId, payload) { return unwrap(await api.put(`${V2}/sourcing/runs/${encodeURIComponent(runId)}/verdicts`, payload)); }
  static async poolRunChecklist(runId, lines) { return unwrap(await api.put(`${V2}/sourcing/runs/${encodeURIComponent(runId)}/checklist`, { lines })); }
  static async finishPoolRun(runId, payload = {}) { return unwrap(await api.post(`${V2}/sourcing/runs/${encodeURIComponent(runId)}/finish`, payload)); }
  static async poolIssues(params = {}) { return unwrap(await api.get(`${V2}/sourcing/issues`, { params })); }
  static async logPoolIssue(payload) { return unwrap(await api.post(`${V2}/sourcing/issues`, payload)); }
  static async poolIssue(issueId) { return unwrap(await api.get(`${V2}/sourcing/issues/${encodeURIComponent(issueId)}`)); }
  static async updatePoolIssue(issueId, payload) { return unwrap(await api.patch(`${V2}/sourcing/issues/${encodeURIComponent(issueId)}`, payload)); }
  static async sourcedJobAction(id, action, payload = {}) { return unwrap(await api.post(`${V2}/sourcing/jobs/${encodeURIComponent(id)}/${action}`, payload)); }

  // ── Report proposals: hand a finished report to a real customer ──────
  // Owner sends; the customer claims OWNERSHIP into their own account.
  static async sendProposal(projectId, payload = {}) {
    const res = await api.post(`${V2}/projects/${projectId}/proposals`, payload);
    return unwrap(res);
  }

  static async listProposals(projectId) {
    const res = await api.get(`${V2}/projects/${projectId}/proposals`);
    return unwrap(res);
  }

  // The sender takes an unclaimed proposal back; the link stops working.
  static async recallProposal(projectId, proposalId, { reason = '' } = {}) {
    const res = await api.post(`${V2}/projects/${projectId}/proposals/${encodeURIComponent(proposalId)}/recall`, { reason });
    return unwrap(res);
  }

  // The claim landing page (magic-link token). Read — grants nothing.
  static async openProposal(token) {
    const res = await api.get(`${V2}/proposals/claim/${encodeURIComponent(token)}`);
    return unwrap(res);
  }

  static async claimProposal(token, { consent = false } = {}) {
    const res = await api.post(`${V2}/proposals/claim/${encodeURIComponent(token)}`, { consent });
    return unwrap(res);
  }

  static async declineProposal(token, { reason = '' } = {}) {
    const res = await api.post(`${V2}/proposals/claim/${encodeURIComponent(token)}/decline`, { reason });
    return unwrap(res);
  }

  // RF-087: one-click unsubscribe from the cold-outreach proposal email.
  // No consent/login needed to opt OUT.
  static async unsubscribeProposal(token) {
    const res = await api.post(`${V2}/proposals/unsubscribe/${encodeURIComponent(token)}`);
    return unwrap(res);
  }

  static async myReviews(params = {}) {
    const res = await guestRetry(async () =>
      api.get(`${V2}/reviews/mine`, await withGuestAuth({ params }))
    );
    // Backend returns { reviews: [...] } — unwrap to a flat list when present.
    const data = unwrap(res);
    if (Array.isArray(data?.reviews)) return { items: data.reviews };
    if (Array.isArray(data)) return { items: data };
    return data;
  }

  // Cancel a review job.
  //   - Pre-assignment (status: requested) → full refund, server doesn't
  //     require confirm.
  //   - Post-assignment (status: assigned / in_progress / submitted /
  //     revision_requested) → server returns 412 V2.CANCEL_REQUIRES_
  //     CONFIRMATION with split amounts in `detail`. Frontend shows the
  //     modal, user accepts, second call passes `confirm: true`.
  static async cancelReview(jobId, { confirm = false } = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/reviews/${jobId}/cancel`, { confirm }, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async getReview(jobId) {
    const res = await guestRetry(async () =>
      api.get(`${V2}/reviews/${jobId}`, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async submitVerdict(jobId, payload = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/reviews/${jobId}/verdict`, payload, await withGuestAuth())
    );
    return unwrap(res);
  }

  // 🔁 Release the assigned reviewer — job returns to marketplace for
  // re-claim. Escrow stays held; the next reviewer claims the same
  // escrow. Owner-only.
  static async releaseReviewer(jobId, payload = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/reviews/${jobId}/release-reviewer`, payload, await withGuestAuth())
    );
    return unwrap(res);
  }

  // 💬 Fetch (or lazy-create) the chat threadId for a review. Returns
  //   { threadId } when a thread exists / was created
  //   { threadId: null, reason: 'NO_REVIEWER_YET'|'COMMS_UNAVAILABLE' }
  // Use the threadId with the /communications/* pass-through to send
  // and read messages.
  static async getReviewThread(jobId) {
    const res = await guestRetry(async () =>
      api.get(`${V2}/reviews/${jobId}/thread`, await withGuestAuth())
    );
    return unwrap(res);
  }

  // 🎁 Tip the reviewer (owner-only, post-accept). MOX-denominated;
  // service-side cap is 50,000 MOX per call.
  static async tipReviewer(jobId, { amountMox, note = '' } = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/reviews/${jobId}/tip`, { amountMox, note }, await withGuestAuth())
    );
    return unwrap(res);
  }

  // 🌐 Every invite the signed-in user has sent across all their
  // projects — powers the user-side "Invites I sent" tab.
  static async myInvitations() {
    const res = await api.get(`${V2}/invitations/mine`);
    const data = unwrap(res);
    if (Array.isArray(data?.invitations)) return { items: data.invitations };
    if (Array.isArray(data)) return { items: data };
    return { items: [] };
  }

  // Expert side — these require an authenticated reviewer with the right track.
  // Backend wraps lists in { reviews: [...] }; we normalise to { items: [...] }.
  static async availableReviews(params = {}) {
    const res = await api.get(`${V2}/reviews/available`, { params });
    const data = unwrap(res);
    // The pool now carries ranking metadata alongside the list: match scores,
    // the active filters, and an empty-state reason that names the remedy.
    // Spread it through — dropping it here is what would leave the UI with a
    // bare "no reviews available" and nothing to act on.
    if (Array.isArray(data?.reviews)) {
      return {
        items: data.reviews,
        // `reviews` kept as an alias: MarketplacePage reads that name.
        reviews: data.reviews,
        sortBy: data.sortBy || 'match',
        filters: data.filters || {},
        emptyState: data.emptyState || null,
        counts: data.counts || null,
        aboveLevel: data.aboveLevel || null,
        bandGap: data.bandGap || null,
        activeClaims: data.activeClaims ?? null,
        outOfOffice: Boolean(data.outOfOffice)
      };
    }
    if (Array.isArray(data)) return { items: data, reviews: data };
    return data;
  }

  static async poolPrefs() {
    return unwrap(await api.get(`${V2}/reviews/pool-prefs`));
  }

  static async savePoolPrefs(body) {
    return unwrap(await api.put(`${V2}/reviews/pool-prefs`, body));
  }

  // ⏱️ SLA clock control.
  static async openClarification(jobId, reason) {
    return unwrap(await api.post(`${V2}/reviews/${jobId}/clarification`, { reason }));
  }

  static async resolveClarification(jobId) {
    return unwrap(await api.post(`${V2}/reviews/${jobId}/clarification/resolve`));
  }

  static async assignedReviews(params = {}) {
    const res = await api.get(`${V2}/reviews/assigned`, { params });
    const data = unwrap(res);
    if (Array.isArray(data?.reviews)) return { items: data.reviews };
    if (Array.isArray(data)) return { items: data };
    return data;
  }

  // Claiming carries the terms version the reviewer accepted. The server
  // refuses a claim whose version does not match the one in force — including
  // a STALE acceptance — so this is the value that makes the gate passable.
  static async acceptReview(jobId, acceptedTermsVersion = null) {
    const res = await api.post(`${V2}/reviews/${jobId}/accept`, { acceptedTermsVersion });
    return unwrap(res);
  }

  static async declineReview(jobId, reason = '') {
    const res = await api.post(`${V2}/reviews/${jobId}/decline`, { reason });
    return unwrap(res);
  }

  static async startReview(jobId) {
    const res = await api.post(`${V2}/reviews/${jobId}/start`, {});
    return unwrap(res);
  }

  static async submitReview(jobId, payload = {}) {
    const res = await api.post(`${V2}/reviews/${jobId}/submit`, payload);
    return unwrap(res);
  }

  // The OWNER's verdict on a submitted review — the other half of
  // `submitReview`, and the step that releases the escrow.
  //
  // `accept` here is not `acceptReview` above: that one is the REVIEWER
  // claiming a job. Two different parties, two different meanings, and the
  // owner's had no client method and no screen at all, so a submitted
  // review sat until the sweeper timed it out.
  //
  //   verdict: 'accepted_full' | 'accepted_partial' | 'revision_requested'
  //          | 'contested'
  static async reviewOwnerVerdict(jobId, payload = {}) {
    const res = await api.post(`${V2}/reviews/${jobId}/verdict`, payload);
    return unwrap(res);
  }

  // Supersede a frozen version with a fresh draft carrying its content.
  // The work console tells a reviewer to "amend it to make further changes";
  // until this existed there was no way to.
  static async amendReportVersion(versionId, reason) {
    const res = await api.post(`${V2}/report-versions/${versionId}/amend`, { reason });
    return unwrap(res);
  }

  // ── Personal invitations (direct-to-named-person) ───────────────────
  // Mirror the marketplace endpoints but scoped to a specific invitee.
  // All four return the same Invitation row shape: { id, projectId,
  // moksyUsername, name, email, note, sections, requestedBy, status,
  // createdAt, sentAt }. The frontend reviewStore swallows transient
  // failures and falls back to localStorage; this client just relays.
  static async listInvites(projectId) {
    const res = await guestRetry(async () =>
      api.get(`${V2}/projects/${projectId}/invitations`, await withGuestAuth())
    );
    const data = unwrap(res);
    if (Array.isArray(data?.invitations)) return { items: data.invitations };
    if (Array.isArray(data?.items)) return data;
    if (Array.isArray(data)) return { items: data };
    return data;
  }

  static async createInvite(projectId, payload = {}) {
    const res = await guestRetry(async () =>
      api.post(`${V2}/projects/${projectId}/invitations`, payload, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async resendInvite(inviteId) {
    const res = await api.post(`${V2}/invitations/${inviteId}/resend`, {});
    return unwrap(res);
  }

  static async cancelInvite(inviteId) {
    const res = await api.post(`${V2}/invitations/${inviteId}/cancel`, {});
    return unwrap(res);
  }

  // ── Admin operator views ────────────────────────────────────────────
  // Cross-tenant list of every review job / invitation. Returns the
  // normalised { items: [...] } shape that the existing list pages
  // already render against.
  // ── 💰 Reviewer earnings ledger ─────────────────────────────────
  //
  // The requirements-side ledger: accrual on sign-off, maturity out of
  // the auto-accept window, dispute freezes, and payouts with a derived
  // idempotency key. Distinct from the payments Stripe-Connect payout
  // path the reviewer dashboard's Payouts tab speaks to — that one moves
  // money to a bank; this one is the record of what is owed and why.
  //
  // Five routes, a full service and 25 integration tests had existed
  // here with no client at all, so none of it was reachable from the
  // product.

  /** Balances recomputed from entries, plus the ledger's own health. */
  /**
   * 🎫 The reviewer's own TRACK grants (the LVL ladder).
   *
   * `GET /me/tracks` has existed since the ladder was built and nothing in the
   * dashboard called it — so a reviewer could see their category accreditation
   * and never the track grants that decide which queue they reach.
   */
  static async myTracks() {
    return unwrap(await api.get(`${V2}/me/tracks`));
  }

  static async earningsBalances() {
    return unwrap(await api.get(`${V2}/earnings`));
  }

  /** Can a payout start right now — and if not, exactly what is short. */
  static async earningsEligibility() {
    return unwrap(await api.get(`${V2}/earnings/eligibility`));
  }

  /**
   * The review-by-review statement: one line per movement, with the two
   * deductions kept apart. Built, routed and proxied long before anything
   * called it — the Payouts tab has been pointing reviewers at "the
   * review-by-review breakdown" that had no client to fetch it.
   */
  static async earningsStatement({ from, to } = {}) {
    // `from`/`to` are the names the endpoint requires, and it 400s without
    // both. Defaulted to a wide window rather than left to the caller: the
    // screen wants "my history", and a statement that refuses unless asked
    // for a period is a statement nobody fetches.
    const params = {
      from: new Date(from || 0).toISOString(),
      to: new Date(to || Date.now() + 86400000).toISOString()
    };
    return unwrap(await api.get(`${V2}/earnings/statement`, { params }));
  }

  /** Start a payout. Idempotency is derived server-side, not here. */
  static async requestEarningsPayout() {
    return unwrap(await api.post(`${V2}/earnings/payout`, {}));
  }

  // Both admin lists are paged server-side and carry `projectName` on every
  // row. Shape: { items, total, page, pageSize }. Params: status, tier, track,
  // q (matches ids, emails AND project names), page, pageSize.
  static async adminListReviews(params = {}) {
    const res = await api.get(`${V2}/admin/reviews`, { params });
    const data = unwrap(res);
    const items = Array.isArray(data?.reviews) ? data.reviews : Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    return { items, total: Number(data?.total ?? items.length), page: Number(data?.page || params.page || 1), pageSize: Number(data?.pageSize || params.pageSize || items.length || 50), hiddenCancelled: Number(data?.hiddenCancelled || 0) };
  }

  static async adminListInvitations(params = {}) {
    const res = await api.get(`${V2}/admin/invitations`, { params });
    const data = unwrap(res);
    const items = Array.isArray(data?.invitations) ? data.invitations : Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    return { items, total: Number(data?.total ?? items.length), page: Number(data?.page || params.page || 1), pageSize: Number(data?.pageSize || params.pageSize || items.length || 50) };
  }

  static async adminReviewStats() {
    const res = await api.get(`${V2}/admin/reviews/stats`);
    return unwrap(res) || { total: 0, stale: 0, byStatus: {}, byTier: {} };
  }

  static async adminAssignReview(jobId, payload = {}) {
    const res = await api.post(`${V2}/admin/reviews/${jobId}/assign`, payload);
    return unwrap(res);
  }

  static async adminCancelReview(jobId, payload = {}) {
    const res = await api.post(`${V2}/admin/reviews/${jobId}/admin-cancel`, payload);
    return unwrap(res);
  }

  // Admin-only user search — used by the Assign modal's reviewer
  // autocomplete. Hits the users-service /users/search route via the
  // gateway. Returns an array of user docs with email + username +
  // displayName fields. Empty array on errors so the UI gracefully
  // degrades to a paste-id input.
  static async adminSearchUsers(q) {
    const term = String(q || '').trim();
    if (term.length < 2) return [];
    try {
      const res = await api.get('/users/search', { params: { q: term } });
      const data = unwrap(res);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.users)) return data.users;
      return [];
    } catch {
      return [];
    }
  }

  static async adminNudgeOwner(jobId, payload = {}) {
    const res = await api.post(`${V2}/admin/reviews/${jobId}/nudge`, payload);
    return unwrap(res);
  }

  // Reopen a CANCELLED / EXPIRED marketplace review job as a fresh
  // listing. Pass `assigneeId` to pre-assign the new job to a
  // reviewer (or to the operator themselves for "Reopen + claim").
  // Defaults to no override on price / track, so the fresh job
  // inherits the source job's economics.
  static async adminReopenReview(jobId, { assigneeId = null, priceMox = null, track = null } = {}) {
    const res = await api.post(`${V2}/admin/reviews/${jobId}/reopen`, {
      assigneeId, priceMox, track
    });
    return unwrap(res);
  }

  // ─── Operator review policy (kill-switch / caps / section matrix) ─
  // Read returns the policy + reference data the admin UI needs
  // (track list, section list with labels). Update is super-admin only
  // at both the gateway and the service.
  static async getReviewPolicy() {
    const res = await api.get(`${V2}/admin/review-policy`);
    return unwrap(res);
  }
  static async updateReviewPolicy(patch = {}) {
    const res = await api.patch(`${V2}/admin/review-policy`, patch);
    return unwrap(res);
  }

  // ─── Admin operator actions on direct invitations ─────────────────
  // Different shape from the owner-scoped invite endpoints because the
  // ownerId guard is bypassed server-side and the path lives under
  // /v2/requirements/admin/invitations/:id/*. Used by Review Pipeline.
  static async adminCancelInvitation(inviteId, { reason = null } = {}) {
    const res = await api.post(`${V2}/admin/invitations/${inviteId}/cancel`, { reason });
    return unwrap(res);
  }
  static async adminResendInvitation(inviteId) {
    const res = await api.post(`${V2}/admin/invitations/${inviteId}/resend`, {});
    return unwrap(res);
  }
  // Escalate a stuck direct invite into a marketplace review job.
  // `assigneeId` optional — when present, the new job is pre-assigned
  // to that reviewer. Defaults to null (job lands in the marketplace
  // unclaimed). `track` defaults to null: the service routes by the
  // project's category (trackForCategory). This used to send 'security',
  // which filed every converted invite under a specialism nobody chose (GC-112).
  static async adminConvertInvitation(inviteId, { assigneeId = null, priceMox = null, track = null } = {}) {
    const res = await api.post(`${V2}/admin/invitations/${inviteId}/convert`, {
      assigneeId, priceMox, track
    });
    return unwrap(res);
  }
  // Shorthand "convert + assign to me" — Titan/admin reviewer takes
  // the invite as a marketplace job they personally will work.
  static async adminClaimInvitation(inviteId, { priceMox = null, track = null } = {}) {
    const res = await api.post(`${V2}/admin/invitations/${inviteId}/claim`, {
      priceMox, track
    });
    return unwrap(res);
  }

  // Username lookup — used by the invite panel's autocomplete. Returns
  // { exists, username, displayName } when found; { exists: false }
  // when not. Failure (404 / 5xx) is silently treated as "can't tell"
  // by the caller, so the frontend stays usable without backend.
  static async lookupUser(username) {
    const u = String(username || '').trim().replace(/^@+/, '');
    if (!u) return { exists: false };
    // The /users/lookup gateway route uses `authorize` (not
    // authorizeAny), so guest tokens always 401. Short-circuit before
    // we fire the request — saves a round-trip AND silences a noisy
    // 401 in the browser console on every keystroke of the invite-
    // panel autocomplete. The caller treats { exists: null } as
    // "unknown" and gracefully falls through to free-text mode.
    if (isGuestMode()) return { exists: null, guestMode: true };
    let accessToken = '';
    try { accessToken = storage.get(storage.KEYS.ACCESS_TOKEN) || ''; } catch (_) {}
    if (!accessToken) return { exists: null, unauthenticated: true };
    try {
      const res = await api.get(`/users/lookup`, { params: { username: u } });
      return unwrap(res) || { exists: false };
    } catch (e) {
      // 404 → not found, anything else → treat as unknown.
      if (e?.response?.status === 404) return { exists: false };
      return { exists: null, error: true };
    }
  }

  // Wallet (mox balance + escrow)
  static async getWallet() {
    const res = await guestRetry(async () =>
      api.get(`${V2}/wallet`, await withGuestAuth())
    );
    return unwrap(res);
  }

  // ── GDPR self-service (Articles 17 + 20) ────────────────────────────
  // The MyDataPage was built against these four methods but they were never
  // added, so the whole page was orphaned (never routed, and it would have
  // thrown on load anyway). Backend routes: GET /me/data-classes,
  // /me/deletion-requests, /me/data-export, and the ACCOUNT-WIDE erasure
  // coordinator POST /profile/erasure-request (which lets a service with
  // money owed refuse before anything irreversible, then fans out).

  // Article 20 transparency: what categories of data Moksy holds.
  static async dataClasses() {
    const res = await api.get(`${V2}/me/data-classes`);
    return unwrap(res) || { classes: [] };
  }

  // The account holder's own erasure-request history.
  static async myDeletionRequests() {
    const res = await api.get(`${V2}/me/deletion-requests`);
    return unwrap(res) || { requests: [] };
  }

  // Article 20 portability lives in `exportMyData` above — ONE method, and it
  // returns the data, not a string. There was a second `exportMyData` here that
  // dropped the guest auth and pre-stringified the payload; being declared
  // second it won, so the guest panel's own export ran member-only (401 for the
  // person it was built for) and, when it did return, `JSON.stringify` was
  // applied to a string and the download was a quoted, escaped blob.

  // Article 17 erasure. Routes through the account-wide coordinator, NOT the
  // requirements-only deletion, so the erasure is complete and the money
  // safeguard runs first. `confirm` (the user's own id, typed on the page) is
  // the client-side gate; the backend's literal confirmation token is sent here.
  static async requestDeletion({ reason } = {}) {
    const res = await api.post('/profile/erasure-request', { confirmation: 'ERASE_MY_DATA', reason });
    return unwrap(res);
  }

  // ── Category templates — per-vertical default-answer overrides on
  // top of the shared question catalog. CRUD is admin-only server-side
  // (isPlatformAdmin); list/get/classify have no server-side gate but
  // the dashboard only surfaces this tab to admin/super_admin.
  /**
   * The category TAXONOMY — the approved vocabulary a project can be
   * classified into, and a reviewer accredited in.
   *
   * Not `listCategories`, which returns per-category TEMPLATE overrides and
   * is legitimately empty until somebody configures one. The reviewer
   * directory called that one and rendered a filter whose only option was
   * "All". Two collections, one word apart.
   */
  static async listCategoryTaxonomy() {
    const res = await api.get(`${V2}/categories/taxonomy`);
    const data = unwrap(res);
    return Array.isArray(data?.categories) ? data.categories : [];
  }

  static async listCategories({ all = false } = {}) {
    const res = await api.get(`${V2}/categories`, all ? { params: { all: 'true' } } : {});
    const data = unwrap(res);
    return Array.isArray(data?.categories) ? data.categories : [];
  }

  static async createCategory(payload = {}) {
    const res = await api.post(`${V2}/admin/categories`, payload);
    return unwrap(res);
  }

  static async updateCategory(categoryCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/categories/${categoryCode}`, patch);
    return unwrap(res);
  }

  static async deleteCategory(categoryCode) {
    const res = await api.delete(`${V2}/admin/categories/${categoryCode}`);
    return unwrap(res);
  }

  // Preview tool — test a free-text idea against active templates
  // without spending a project creation.
  static async classifyCategory(idea) {
    const res = await api.post(`${V2}/categories/classify`, { idea });
    return unwrap(res);
  }

  // ─── Admin home (M-093) ────────────────────────────────────────────
  static async adminStats() {
    const res = await api.get(`${V2}/admin/stats`);
    return unwrap(res);
  }

  // ─── Audit log (M-009) ─────────────────────────────────────────────
  static async auditLog(params = {}) {
    const res = await api.get(`${V2}/admin/audit-log`, { params });
    return unwrap(res);
  }

  // ─── Expert applications (M-005, applicant side) ───────────────────
  // These three were the missing half of the funnel. The service, the
  // routes and the gateway proxies all existed; nothing in the UI ever
  // called them, so "Apply to be a reviewer" wrote a reviewer PROFILE to
  // the payments service instead and the admin queue — which reads
  // v2_expert_application in the requirements service — stayed empty no
  // matter how many people applied.
  // 🧩 role / level / market travel with the application. This destructured
  // only {track, statement, credentials} and rebuilt the body from those three
  // — so the form sent the applicant's chosen role and level, the gateway
  // forwarded the whole body, the service accepted them and the model declared
  // them, and they arrived null anyway. One narrow destructure in the middle
  // of six correct layers. Walked 2026-09-10: the derived title landed on the
  // funnel and the choices behind it did not.
  // 📝 …and the same narrowing dropped Declare and the rest of the evidence
  // (GC-064): the form sent `categories` and `title`, this rebuilt the body
  // without them, and the funnel's Declare stage never saw her category list,
  // title, links or specialties. They travel now; the server writes them into
  // the funnel it reports progress from.
  static async expertApply({
    track, statement, credentials = [], role = null, level = null, market = null,
    categories = [], title = '', years = null, links = {}, specialties = []
  } = {}) {
    const res = await api.post(`${V2}/expert-applications`, {
      track, statement, credentials, role, level, market,
      categories, title, years, links, specialties
    });
    return unwrap(res);
  }

  // My own applications to review. The route and its gateway leg have always
  // existed; nothing in the dashboard called them, so no customer-facing
  // surface could tell "here to earn" from "here to spend" (GC-120).
  static async myExpertApplications() {
    const res = await api.get(`${V2}/expert-applications/mine`);
    return unwrap(res);
  }

  // ─── Expert applications (M-005, admin side) ───────────────────────
  static async expertQueue() {
    const res = await api.get(`${V2}/admin/expert-applications`);
    return unwrap(res);
  }

  // One application with its applicant, history and grants, composed by the
  // gateway so the detail page costs a single request.
  static async expertApplication(applicationId) {
    const res = await api.get(`${V2}/admin/expert-applications/${applicationId}`);
    return unwrap(res);
  }

  static async expertAdjustLevel(userId, track, payload = {}) {
    const res = await api.post(`${V2}/admin/level-grants/${encodeURIComponent(userId)}/${encodeURIComponent(track)}`, payload);
    return unwrap(res);
  }
  static async expertDecide(applicationId, payload = {}) {
    const res = await api.post(`${V2}/admin/expert-applications/${applicationId}/decision`, payload);
    return unwrap(res);
  }

  // ─── Contested-review adjudication (M-006) ─────────────────────────
  static async contestedReviews() {
    const res = await api.get(`${V2}/admin/reviews/contested`);
    return unwrap(res);
  }

  static async adjudicateReview(jobId, payload = {}) {
    const res = await api.post(`${V2}/admin/reviews/${jobId}/adjudicate`, payload);
    return unwrap(res);
  }

  // ─── Feature flag console (M-022) ──────────────────────────────────
  static async myFlags() {
    const res = await api.get(`${V2}/me/flags`);
    return unwrap(res);
  }

  static async listFlags() {
    const res = await api.get(`${V2}/admin/feature-flags`);
    return unwrap(res);
  }

  static async createFlag(payload = {}) {
    const res = await api.post(`${V2}/admin/feature-flags`, payload);
    return unwrap(res);
  }

  static async updateFlag(key, patch = {}) {
    const res = await api.put(`${V2}/admin/feature-flags/${key}`, patch);
    return unwrap(res);
  }

  static async archiveFlag(key) {
    const res = await api.post(`${V2}/admin/feature-flags/${key}/archive`, {});
    return unwrap(res);
  }

  static async unarchiveFlag(key) {
    const res = await api.post(`${V2}/admin/feature-flags/${key}/unarchive`, {});
    return unwrap(res);
  }

  static async previewFlags(asUser = {}) {
    const res = await api.post(`${V2}/admin/feature-flags/preview`, asUser);
    return unwrap(res);
  }

  // ─── Content hierarchy — Pillars -> Sub-pillars -> Requirements ->
  // Base Templates -> Document Category Templates. Entirely separate
  // from the /categories (feature-flag-style) endpoints above — a
  // different backend model, see DocumentCategoryTemplateModel.js.
  static async listPillars({ all = false, templateCode } = {}) {
    const res = await api.get(`${V2}/pillars`, { params: { ...(all ? { all: 'true' } : {}), ...(templateCode ? { templateCode } : {}) } });
    const data = unwrap(res);
    return Array.isArray(data?.pillars) ? data.pillars : [];
  }

  static async materializeTemplate(templateCode, from = 'base') {
    const res = await api.post(`${V2}/admin/templates/${templateCode}/materialize`, { from });
    return unwrap(res);
  }

  static async getPillar(pillarCode) {
    const res = await api.get(`${V2}/pillars/${pillarCode}`);
    return unwrap(res);
  }

  // Pure Base solution document — every pillar/sub-pillar/requirement
  // with its own canonical defaultValue/weight, no Master/Category
  // overlay. Doesn't require a Master template to exist.
  static async resolveBaseTree(templateCode) {
    const res = await api.get(`${V2}/pillars/resolve-base`, templateCode ? { params: { templateCode } } : {});
    return unwrap(res);
  }

  static async createPillar(payload = {}) {
    const res = await api.post(`${V2}/admin/pillars`, payload);
    return unwrap(res);
  }

  // Apply the Strategy brief onto the existing (title-matched) Strategy
  // pillar: establishes, relevance masks, 37 questions, Master tuning.
  static async enrichStrategy() {
    const res = await api.post(`${V2}/admin/pillars/enrich-strategy`, {});
    return unwrap(res);
  }

  // Poll a running experiment / generate job -> { status, elapsedMs, result?, error? }.
  // While the local model is generating it can saturate the service, so give the
  // poll headroom over the 10s axios default (a slow poll must not read as failure).
  static async getExperimentStatus(pillarCode, jobId) {
    const res = await api.get(`${V2}/admin/pillars/${pillarCode}/experiment/${jobId}`, { timeout: 30000 });
    return unwrap(res);
  }

  // ── Learning loop (piece 2): frozen-corpus replay + review ────────────
  // Browse the corpus (optionally filtered) -> { items:[{id,title,category,
  // difficulty,frozen,reviewed,...}], total }.
  /**
   * The benchmark corpus, searched, filtered and paged.
   *
   * Reviewing an item here is the only thing that creates gold, so the list has
   * to answer "what should I judge next" rather than only "what is in it".
   */
  static async listLearningCorpus(params = {}) {
    const res = await api.get(`${V2}/admin/learning/corpus`, { params });
    return unwrap(res);
  }


  // Replay an item through the recorded pipeline -> { jobId }. Poll
  // getLearningReplayStatus (the run takes minutes on the weak local model).
  static async startLearningReplay(id) {
    const res = await api.post(`${V2}/admin/learning/corpus/${id}/replay`, {});
    return unwrap(res);
  }

  static async getLearningReplayStatus(jobId) {
    const res = await api.get(`${V2}/admin/learning/replay/${jobId}`, { timeout: 30000 });
    return unwrap(res);
  }

  // ── Workflow versioning (admin) ──────────────────────────────────────────
  // Which engine versions exist + the active default new projects get.
  static async getWorkflowStatus() {
    const res = await api.get(`${V2}/admin/workflow/status`);
    return unwrap(res);
  }
  // One-click activate: make `version` the default for NEW projects.
  static async activateWorkflowVersion(version) {
    const res = await api.post(`${V2}/admin/workflow/activate`, { version });
    return unwrap(res);
  }
  // Switch ONE project to another version (reversible, keeps prior doc).
  static async switchProjectWorkflow(projectId, version) {
    const res = await api.post(`${V2}/admin/workflow/projects/${projectId}/switch`, { version });
    return unwrap(res);
  }

  // ── Workflow templates ────────────────────────────────────────────
  // Operator-authored journeys. A published template registers itself as a
  // workflow version, so it then appears in getWorkflowStatus() alongside the
  // built-in catalog@1 / pillar@1 and can be activated the same way.
  static async listWorkflowTemplates({ all = false, published = false } = {}) {
    const params = { ...(all ? { all: 'true' } : {}), ...(published ? { published: 'true' } : {}) };
    const res = await api.get(`${V2}/workflow-templates`, { params });
    const data = unwrap(res);
    return Array.isArray(data?.templates) ? data.templates : [];
  }
  // Field metadata + effective defaults for the policy editor — served so the
  // ranges and labels live in exactly one place.
  static async getWorkflowPolicySchema() {
    const res = await api.get(`${V2}/workflow-templates/policy-schema`);
    return unwrap(res);
  }
  // Install the two shipped templates (guest-free, spec-sprint). Insert-only;
  // force refreshes UNPUBLISHED ones from the seed file and refuses published
  // ones, which are somebody's live interview.
  static async seedWorkflowTemplates({ force = false } = {}) {
    const res = await api.post(`${V2}/admin/workflow-templates/seed`, { force });
    return unwrap(res);
  }

  static async createWorkflowTemplate(payload = {}) {
    const res = await api.post(`${V2}/admin/workflow-templates`, payload);
    return unwrap(res);
  }
  static async updateWorkflowTemplate(code, patch = {}) {
    const res = await api.patch(`${V2}/admin/workflow-templates/${code}`, patch);
    return unwrap(res);
  }
  static async publishWorkflowTemplate(code, published = true) {
    const res = await api.post(`${V2}/admin/workflow-templates/${code}/publish`, { published });
    return unwrap(res);
  }
  static async cloneWorkflowTemplate(code, payload = {}) {
    const res = await api.post(`${V2}/admin/workflow-templates/${code}/clone`, payload);
    return unwrap(res);
  }
  static async deleteWorkflowTemplate(code) {
    const res = await api.delete(`${V2}/admin/workflow-templates/${code}`);
    return unwrap(res);
  }

  // ── Disputes ───────────────────────────────────────────────────────
  // The path when a customer will not accept the work. Every one of these
  // routes existed and was unreachable until the gateway half landed, so no
  // client method existed either. Participant-side calls are scoped
  // server-side to the customer, the reviewer or a panel member on the job.
  static async raiseDispute(jobId, { reason, sections = null, fault = undefined } = {}) {
    // `fault` is the raiser's CLAIM about what went wrong, not a finding —
    // the adjudicator writes the finding. Sent only when stated, so a
    // customer who skips the question is not recorded as having answered it.
    const res = await api.post(`${V2}/reviews/${jobId}/dispute`, { reason, sections, ...(fault ? { fault } : {}) });
    return unwrap(res);
  }
  // The disputes I am a party to, as customer or as reviewer. Every other
  // participant verb is keyed by disputeId and nothing else hands one out.
  static async myDisputes() {
    const res = await api.get(`${V2}/disputes/mine`);
    return unwrap(res);
  }
  static async submitDisputeEvidence(disputeId, { body, kind = 'statement', attachmentRef = null } = {}) {
    const res = await api.post(`${V2}/disputes/${disputeId}/evidence`, { body, kind, attachmentRef });
    return unwrap(res);
  }
  static async appealDispute(disputeId, reason) {
    const res = await api.post(`${V2}/disputes/${disputeId}/appeal`, { reason });
    return unwrap(res);
  }
  // Admin side.
  // The two-person impersonation queue. Lives on the gateway's /admin/*
  // surface rather than under /v2/requirements, so it is called directly.
  // ── 👥 Two-person approvals for sensitive capabilities ─────────────
  static async twoPersonPolicy() {
    return unwrap(await api.get('/admin/sensitive/policy'));
  }
  static async setTwoPersonPolicy(capabilities, note = '') {
    return unwrap(await api.put('/admin/sensitive/policy', { capabilities, note }));
  }
  static async twoPersonRequests(status = null) {
    return unwrap(await api.get('/admin/sensitive/requests', { params: status ? { status } : {} }));
  }
  static async requestTwoPerson({ capability, reason, intent = '', subjectKind = '', subjectId = '', payload = null }) {
    return unwrap(await api.post('/admin/sensitive/requests', { capability, reason, intent, subjectKind, subjectId, payload }));
  }
  static async decideTwoPerson(requestId, approve, note = '') {
    const verb = approve ? 'approve' : 'deny';
    return unwrap(await api.post(`/admin/sensitive/requests/${encodeURIComponent(requestId)}/${verb}`, { note }));
  }
  static async withdrawTwoPerson(requestId) {
    return unwrap(await api.post(`/admin/sensitive/requests/${encodeURIComponent(requestId)}/withdraw`, {}));
  }

  static async impersonationRequests(status = null) {
    const res = await api.get('/admin/impersonation/requests', { params: status ? { status } : {} });
    return res.data?.data || res.data;
  }

  // The deciding half of the queue. Listing shipped without these, so the
  // console could show pending requests and offer nothing to do about them.
  //
  // A requester cannot approve their own request — the service answers 403
  // IMPERSONATION.SELF_APPROVAL — so a console that renders Approve on every
  // row will show it on the operator's own, and it will fail. Hide it there,
  // or expect that refusal and say why rather than surfacing a bare 403.
  static async approveImpersonation(requestId) {
    const res = await api.post(`/admin/impersonation/requests/${encodeURIComponent(requestId)}/approve`);
    return res.data?.data || res.data;
  }

  // Pulling back your OWN request. Distinct from deny, which is a colleague
  // refusing — the queue would stop being evidence if the two looked alike.
  static async withdrawImpersonation(requestId) {
    const res = await api.post(`/admin/impersonation/requests/${encodeURIComponent(requestId)}/withdraw`);
    return res.data?.data || res.data;
  }

  static async denyImpersonation(requestId, reason = '') {
    const res = await api.post(`/admin/impersonation/requests/${encodeURIComponent(requestId)}/deny`, { reason });
    return res.data?.data || res.data;
  }

  // Queue-health reads for the staff console. Each is separate so one
  // unavailable queue degrades to "unavailable" rather than blanking the page.
  static async disputesSla() {
    const res = await api.get(`${V2}/admin/disputes/sla`);
    return unwrap(res);
  }
  static async qcHealth() {
    const res = await api.get(`${V2}/admin/qc/health`);
    return unwrap(res);
  }
  static async expertApplicationQueue() {
    const res = await api.get(`${V2}/admin/expert-applications`);
    return unwrap(res);
  }
  static async listDisputes(params = {}) {
    const res = await api.get(`${V2}/admin/disputes`, { params });
    return unwrap(res);
  }
  static async resolveDispute(disputeId, { resolution, reviewerBps = null, notes = '', fault = undefined } = {}) {
    const res = await api.post(`${V2}/admin/disputes/${disputeId}/resolve`, { resolution, reviewerBps, notes, ...(fault ? { fault } : {}) });
    return unwrap(res);
  }
  // decision is 'upheld' or 'rejected'. Upholding MAY also overturn the
  // original outcome — newResolution replaces it, and newReviewerBps is
  // required when that new outcome is a partial split.
  // Sending the clawback notice. Only meaningful when the settlement actually
  // produced a clawback — the service refuses otherwise.
  // Record a chargeback from the payment provider. Note this CREATES a dispute
  // rather than acting on one — the money has already left, so it goes
  // straight to staff with no two-sided phase to wait for.
  static async recordChargeback(jobId, { reason, providerRef = null } = {}) {
    const res = await api.post(`${V2}/admin/reviews/${jobId}/chargeback`, { reason, providerRef });
    return unwrap(res);
  }
  static async markClawbackNotice(disputeId) {
    const res = await api.post(`${V2}/admin/disputes/${disputeId}/clawback-notice`, {});
    return unwrap(res);
  }
  static async releaseOwedRefund(disputeId) {
    const res = await api.post(`${V2}/admin/disputes/${disputeId}/release-refund`, {});
    return unwrap(res);
  }
  // Serial-disputer / serially-disputed detection. One dispute says nothing;
  // these are the only view that shows a pattern.
  static async disputePatternForReviewer(reviewerId) {
    const res = await api.get(`${V2}/admin/disputes/patterns/reviewer/${reviewerId}`);
    return unwrap(res);
  }
  static async disputePatternForCustomer(customerId) {
    const res = await api.get(`${V2}/admin/disputes/patterns/customer/${customerId}`);
    return unwrap(res);
  }
  static async decideDisputeAppeal(disputeId, { decision, newResolution = null, newReviewerBps = null, notes = '' } = {}) {
    const res = await api.post(`${V2}/admin/disputes/${disputeId}/appeal-decision`, {
      decision, newResolution, newReviewerBps, notes
    });
    return unwrap(res);
  }

  // ── Delegation rosters ("reviewer companies") ──────────────────────
  // A private shortlist a lead pulls from. Not a billing entity and not
  // accredited — money still settles to individuals through the split.
  /**
   * The roster is READ by the delegate page and nothing could fill it: only
   * `myCompanies` existed, so a lead saw "your people" ranked above the
   * directory and the list was always empty. Creating, adding and removing
   * are all reachable now.
   */
  static async createCompany(name) {
    return unwrap(await api.post(`${V2}/companies`, { name }));
  }

  static async addCompanyMember(companyId, reviewerId, note = '') {
    return unwrap(await api.post(`${V2}/companies/${companyId}/members`, { reviewerId, note }));
  }

  static async removeCompanyMember(companyId, reviewerId) {
    return unwrap(await api.delete(`${V2}/companies/${companyId}/members/${reviewerId}`));
  }

  static async myCompanies() {
    const res = await api.get(`${V2}/companies/mine`);
    return unwrap(res);
  }

  // ── Delegation — a lead reviewer splitting the work ────────────────
  // The page sends { subReviewerId, sections, shares } and the marketplace
  // endpoint expects exactly that. This used to destructure `{ members }` and
  // post `{ members }` instead — a stale shape nothing on either side used, so
  // every delegate-from-the-UI attempt posted an empty body and came back
  // DELEG.SUB_REVIEWER_REQUIRED. Pass the payload straight through.
  static async delegateSections(jobId, payload = {}) {
    const res = await api.post(`${V2}/reviews/${jobId}/delegations`, payload);
    return unwrap(res);
  }
  // What has been delegated TO me. Distinct from delegationRoster, which is
  // the LEAD's view (queried by leadReviewerId) — mixing the two is how a
  // sub-reviewer ends up looking at somebody else's roster.
  static async myDelegations(jobId = null) {
    const res = await api.get(`${V2}/delegations/mine`, { params: jobId ? { jobId } : {} });
    return unwrap(res);
  }
  static async delegationRoster(jobId = null) {
    const res = await api.get(`${V2}/delegations/roster`, { params: jobId ? { jobId } : {} });
    return unwrap(res);
  }
  // Unclaiming requires a reason — the platform records why a section came
  // back rather than letting it silently reappear in the pool.
  static async withdrawDelegation(subAssignmentId, reason) {
    const res = await api.post(`${V2}/delegations/${subAssignmentId}/withdraw`, { reason });
    return unwrap(res);
  }
  static async acceptDelegation(subAssignmentId) {
    const res = await api.post(`${V2}/delegations/mine/${subAssignmentId}/accept`, {});
    return unwrap(res);
  }
  static async declineDelegation(subAssignmentId, reason) {
    const res = await api.post(`${V2}/delegations/mine/${subAssignmentId}/decline`, { reason });
    return unwrap(res);
  }
  static async startDelegation(subAssignmentId) {
    const res = await api.post(`${V2}/delegations/mine/${subAssignmentId}/start`, {});
    return unwrap(res);
  }
  static async submitDelegation(subAssignmentId, payload = {}) {
    const res = await api.post(`${V2}/delegations/mine/${subAssignmentId}/submit`, payload);
    return unwrap(res);
  }

  // Lead accepts the sub's handed-back work → accepted_by_lead (the state the
  // customer-accept settlement pays out from). Without this the lead had no way
  // to accept a submitted section in the UI and the delegation could not
  // complete.
  static async acceptDelegationHandback(subAssignmentId) {
    const res = await api.post(`${V2}/delegations/${subAssignmentId}/handback/accept`, {});
    return unwrap(res);
  }

  static async requestDelegationRevision(subAssignmentId, reason = '') {
    const res = await api.post(`${V2}/delegations/${subAssignmentId}/handback/revision`, { reason });
    return unwrap(res);
  }

  // ── Report versions — the reviewer's working loop ──────────────────
  // Amend, edit a section, regenerate one, track progress, tick the checklist,
  // sign off. The service and its routes existed from the start; the gateway
  // half did not, so none of this was reachable and no client method existed.
  static async reportVersionChecklist(versionId) {
    const res = await api.get(`${V2}/report-versions/${versionId}/checklist`);
    return unwrap(res);
  }
  static async markChecklistItem(versionId, itemId, done = true) {
    const res = await api.put(`${V2}/report-versions/${versionId}/checklist/${itemId}`, { done });
    return unwrap(res);
  }
  // Per-section work progress plus the rollup. Half derived from real edits,
  // half stated by a person.
  // Versions for a project — how a caller obtains a versionId at all.
  static async reportVersionsForProject(projectId) {
    const res = await api.get(`${V2}/projects/${projectId}/report-versions`);
    return unwrap(res);
  }
  static async reportVersionProgress(versionId) {
    const res = await api.get(`${V2}/report-versions/${versionId}/progress`);
    return unwrap(res);
  }
  static async setSectionProgress(versionId, sectionId, state, note = '') {
    const res = await api.put(`${V2}/report-versions/${versionId}/sections/${sectionId}/progress`, { state, note });
    return unwrap(res);
  }
  // baseRevision drives conflict detection — pass what the editor loaded, so a
  // second editor's save is caught rather than silently overwriting.
  static async updateReportSection(versionId, sectionId, content, baseRevision = null) {
    const res = await api.put(`${V2}/report-versions/${versionId}/sections/${sectionId}`, { content, baseRevision });
    return unwrap(res);
  }
  static async regenerateSection(versionId, sectionId) {
    const res = await api.post(`${V2}/report-versions/${versionId}/sections/${sectionId}/regenerate`, {});
    return unwrap(res);
  }
  static async signOffReportVersion(versionId, payload = {}) {
    const res = await api.post(`${V2}/report-versions/${versionId}/sign-off`, payload);
    return unwrap(res);
  }
  // The terms a reviewer accepts at claim, readable before claiming.
  static async reviewClaimTerms() {
    const res = await api.get(`${V2}/reviews/claim-terms`);
    return unwrap(res);
  }

  // What the front page shows today. The day is decided server-side so the
  // page cannot differ per device clock.
  static async getFrontPageExamples({ slots = 4 } = {}) {
    const res = await api.get(`${V2}/examples/front-page`, { params: { slots } });
    return unwrap(res);
  }
  // The full published example, for the public detail page. Gated on
  // showcase approval server-side, not merely on being published.
  static async getPublicExample(exampleCode) {
    const res = await api.get(`${V2}/examples/${exampleCode}`);
    return unwrap(res)?.example ?? null;
  }
  static async listExamples({ reportTypeCode = '', application = '', status = '' } = {}) {
    const params = {};
    if (reportTypeCode) params.reportTypeCode = reportTypeCode;
    if (application) params.application = application;
    if (status) params.status = status;
    const res = await api.get(`${V2}/admin/examples`, { params });
    const data = unwrap(res);
    return Array.isArray(data?.examples) ? data.examples : [];
  }
  // The 5 families x 20 types grid. REAL and PLACEHOLDER_ONLY are counted
  // apart on purpose: a placeholder looks like coverage and is not.
  static async getExampleCoverage() {
    const res = await api.get(`${V2}/admin/examples/coverage`);
    return unwrap(res);
  }
  static async confirmExampleAnonymised(exampleCode) {
    const res = await api.post(`${V2}/admin/examples/${exampleCode}/anonymised`);
    return unwrap(res)?.example ?? null;
  }
  static async publishExample(exampleCode) {
    const res = await api.post(`${V2}/admin/examples/${exampleCode}/publish`);
    return unwrap(res)?.example ?? null;
  }
  static async setExampleCanonical(exampleCode) {
    const res = await api.post(`${V2}/admin/examples/${exampleCode}/canonical`);
    return unwrap(res)?.example ?? null;
  }
  // One role at a time. There is no bulk approve, deliberately: approving
  // an example for training steers every similar report we generate.
  static async setExampleRole(exampleCode, role, { approved = true, note = '' } = {}) {
    const res = await api.patch(`${V2}/admin/examples/${exampleCode}/roles/${role}`, { approved, note });
    return unwrap(res)?.example ?? null;
  }

  // journey records what the platform is meant to do and what the code
  // actually does, so the console is both a design surface and a gap report.
  // Admin-only end to end, reads included.
  static async listJourneys({ all = false, kind = null } = {}) {
    const params = { ...(all ? { all: 'true' } : {}), ...(kind ? { kind } : {}) };
    const res = await api.get(`${V2}/journeys`, { params });
    const data = unwrap(res);
    return Array.isArray(data?.journeys) ? data.journeys : [];
  }
  // Actors, statuses, touchpoint kinds and outcome kinds with their labels and
  // colour tones — served so the console cannot drift from the validation.
  static async getJourneySchema() {
    const res = await api.get(`${V2}/journeys/schema`);
    return unwrap(res);
  }
  // Journeys as nodes, handoffs between them as edges.
  static async getJourneyGraph() {
    const res = await api.get(`${V2}/journeys/graph`);
    return unwrap(res);
  }
  // Measured coverage: what the map CLAIMS against what telemetry has seen.
  // The valuable rows are built steps nothing has ever observed running —
  // each one is either dead code or an untrue claim, and until this existed
  // there was no way to tell which.
  static async getJourneyStepCoverage() {
    const res = await api.get(`${V2}/journeys/step-coverage`);
    return unwrap(res);
  }
  // Every step that is not built, across every journey, cheapest class first.
  static async getJourneyGaps() {
    const res = await api.get(`${V2}/journeys/gaps`);
    return unwrap(res);
  }
  // force replaces a seed-authored step wholesale — text AND status — and never
  // touches a step an operator has edited. The status is included on purpose:
  // the usual reason to re-seed is that the shipped audit now says "built"
  // where it said "missing", so preserving the old status would leave the map
  // reporting gaps that are closed.
  static async seedJourneys({ force = false } = {}) {
    const res = await api.post(`${V2}/admin/journeys/seed`, { force });
    return unwrap(res);
  }
  /**
   * Author a journey from nothing.
   *
   * The route and the service have always been here; nothing called them, so
   * the only way to get a journey was to seed the shipped fifteen. `prefix`
   * matters more than it looks: it is the first half of every step address in
   * the journey, and a journey created without one has steps that cannot be
   * named in a log, a metric or a dispatch call. Omit it and the service
   * derives it from the code by the same rule the seed uses.
   */
  static async createJourney(payload) {
    const res = await api.post(`${V2}/admin/journeys`, payload);
    return unwrap(res);
  }
  /** Journey settings — label, summary, kind, actor, order, prefix, bindings. */
  static async updateJourney(code, patch) {
    const res = await api.patch(`${V2}/admin/journeys/${code}`, patch);
    return unwrap(res);
  }
  /** Soft delete — the code stays reserved and a later create resurrects it. */
  static async deleteJourney(code) {
    const res = await api.delete(`${V2}/admin/journeys/${code}`);
    return unwrap(res);
  }
  // Upsert by step key. Pass position to insert rather than append.
  static async putJourneyStep(code, step, position = null) {
    const res = await api.post(`${V2}/admin/journeys/${code}/steps`, { step, position });
    return unwrap(res);
  }
  static async setJourneyStepStatus(code, stepKey, { status, notes, evidence } = {}) {
    const res = await api.patch(`${V2}/admin/journeys/${code}/steps/${stepKey}/status`, { status, notes, evidence });
    return unwrap(res);
  }
  static async deleteJourneyStep(code, stepKey) {
    const res = await api.delete(`${V2}/admin/journeys/${code}/steps/${stepKey}`);
    return unwrap(res);
  }
  static async reorderJourneySteps(code, keys) {
    const res = await api.post(`${V2}/admin/journeys/${code}/steps/reorder`, { keys });
    return unwrap(res);
  }

  // Browse recorded experiment runs (newest first) so any past experiment can be
  // reopened and reviewed. ideaSource: 'live' (default) | 'frozen_set' | 'all'.
  // Which of our own answers experts keep overturning, grouped by whose
  // mistake it was. `user` never appears — a reviewer overruling something
  // the customer stated is not a Moksy error.
  static async getWhoWasWrong({ days = 90, limit = 20, stage = '' } = {}) {
    const res = await api.get(`${V2}/admin/learning/who-was-wrong`, { params: { days, limit, stage } });
    return unwrap(res);
  }

  /**
   * Every run the loop has observed — searched, filtered and paged.
   *
   * Was `{ ideaSource, limit }` returning the newest hundred with `total` set
   * to the page length. Anything past the limit was unreachable and the count
   * on screen was the size of the page presented as the size of the set.
   */
  static async listLearningRuns(params = {}) {
    const res = await api.get(`${V2}/admin/learning/runs`, { params });
    return unwrap(res);
  }


  // The recorded run + stage outputs + score card, for review.
  static async getLearningRun(runId) {
    const res = await api.get(`${V2}/admin/learning/runs/${runId}`);
    return unwrap(res);
  }

  // Submit a review -> promotes corrections to gold. corrections: [{ stage,
  // field?, producedValue?, correctedValue, diffMetric?, domain? }].
  static async submitLearningReview(runId, { corpusItemId, corrections } = {}) {
    const res = await api.post(`${V2}/admin/learning/runs/${runId}/review`, { corpusItemId, corrections });
    return unwrap(res);
  }

  // ── Reviewer recommendations ────────────────────────────────────────
  // A reviewer pointing the owner at somebody better suited. Upstream of an
  // invitation, not a replacement for one: the owner still invites.
  static async recommendReviewer(projectId, { reviewerId, note = '' } = {}) {
    const res = await api.post(`${V2}/projects/${projectId}/recommendations`, { reviewerId, note });
    return unwrap(res);
  }

  static async projectRecommendations(projectId, { status = null } = {}) {
    const res = await api.get(`${V2}/projects/${projectId}/recommendations`, { params: status ? { status } : {} });
    return unwrap(res);
  }

  static async decideRecommendation(recommendationId, decision, inviteId = null) {
    const res = await api.post(`${V2}/recommendations/${recommendationId}/decide`, { decision, inviteId });
    return unwrap(res);
  }

  // The reviewer's OWN list of runs they may comment on — scoped server-side to
  // projects they actually held a review job on. Separate from listLearningRuns,
  // which is the admin's browse-everything view.
  static async myLearningRuns() {
    const res = await api.get(`${V2}/learning/runs/mine`);
    return unwrap(res);
  }

  // Same payload as submitLearningFeedback, on the non-admin path. Two methods
  // rather than a flag, because the caller always knows which hat it is wearing
  // and a wrong flag here is a silent 403.
  static async submitLearningFeedbackAsReviewer(runId, { ratings = [], observations = [], corrections = [] } = {}) {
    const res = await api.post(`${V2}/learning/runs/${runId}/feedback`, { ratings, observations, corrections });
    return unwrap(res);
  }

  // Live-run feedback (Phase 2): ratings + observations + per-stage corrections
  // for a recorded run in ONE call. Works on live experiment runs (no corpus).
  // { ratings:[{dimension,score}], observations:[{stage?,tag,note?}],
  //   corrections:[{stage,field?,producedValue?,correctedValue}] } -> { written, skipped }.
  static async submitLearningFeedback(runId, { ratings = [], observations = [], corrections = [] } = {}) {
    const res = await api.post(`${V2}/admin/learning/runs/${runId}/feedback`, { ratings, observations, corrections });
    return unwrap(res);
  }

  // Recurring corrections grouped into draftable clusters (read-only preview).
  /**
   * What the learning loop currently knows, and what it knows it from.
   *
   * One call rather than one per tile: the metrics, the recent history of the
   * corpus-wide ones, the evidence counts behind them, and when the job last
   * ran. A console that had to fan out six requests to draw one screen would
   * paint in pieces, and a half-drawn set of quality numbers reads worse than
   * none.
   */
  static async learningMetrics({ historyLimit = 30 } = {}) {
    const res = await api.get(`${V2}/admin/learning/metrics`, { params: { historyLimit } });
    return unwrap(res);
  }

  /** Run the nightly job now. Persists a fresh snapshot for every version. */
  static async recomputeLearningMetrics() {
    const res = await api.post(`${V2}/admin/learning/metrics/recompute`, {});
    return unwrap(res);
  }

  /**
   * The bounded-tuning plan (brief §6): which recurring tags name a live step
   * parameter, what would move inside which bounds, which would apply without
   * a person, which tags have no live lever and where their fix lives, and
   * what the loop has already applied (with canary state).
   */
  static async learningTuningPlan(params = {}) {
    const res = await api.get(`${V2}/admin/learning/tuning`, { params });
    return unwrap(res);
  }

  /** Run the sweep. Bounded candidates apply and re-pin; the rest queue as proposals. */
  static async applyLearningTuning(body = {}) {
    const res = await api.post(`${V2}/admin/learning/tuning/apply`, body);
    return unwrap(res);
  }

  /** Roll an applied bounded change back by proposal id. */
  static async rollbackLearningTuning(proposalId, reason = '') {
    const res = await api.post(`${V2}/admin/learning/tuning/${encodeURIComponent(proposalId)}/rollback`, { reason });
    return unwrap(res);
  }

  /** Check the canary over every bounded change still being watched. */
  static async canaryLearningTuning() {
    const res = await api.post(`${V2}/admin/learning/tuning/canary`, {});
    return unwrap(res);
  }

  /**
   * Which approved examples an idea would pull in, and the exact text that
   * would be injected into the prompt because of them.
   */
  static async learningRetrievalPreview({ idea, stage = 'report', k = 2 }) {
    const res = await api.get(`${V2}/admin/learning/retrieval-preview`, { params: { idea, stage, k } });
    return unwrap(res);
  }

  static async listLearningProposalClusters({ minRecurrence = 2 } = {}) {
    const res = await api.get(`${V2}/admin/learning/proposal-clusters`, { params: { minRecurrence } });
    return unwrap(res);
  }

  // Draft pending TemplateProposals from clusters -> { drafted, results }. The
  // proposals then flow through the normal permissioned approval.
  static async draftLearningProposals({ minRecurrence = 2, keys = null } = {}) {
    const res = await api.post(`${V2}/admin/learning/proposal-clusters/draft`, { minRecurrence, keys });
    return unwrap(res);
  }

  // ── Stateful interview (one seed at a time) ──────────────────────────
  // Start: detect the template from the idea, persist a project, and return the
  // first seed question + { projectId, pillarCode, detectedTemplate,
  // nextQuestion, answeredSeeds, totalSeeds, complete, understanding, gate }.
  // Both start and answer run per-seed extraction/adaptive LLM calls (5–60s on a
  // local model), so they override the axios default (10s) with a long timeout.
  static async startInterview({ idea } = {}) {
    const res = await api.post(`${V2}/admin/experiment/interview`, { idea }, { timeout: 120000 });
    return unwrap(res);
  }

  // Submit one seed answer -> { nextQuestion, answeredSeeds, totalSeeds, complete }.
  static async submitInterviewAnswer(pillarCode, projectId, { qid, value } = {}) {
    const res = await api.post(`${V2}/admin/pillars/${pillarCode}/interview/${projectId}/answer`, { qid, value }, { timeout: 120000 });
    return unwrap(res);
  }

  // Read-only interview snapshot for polling -> { nextQuestion, answeredSeeds,
  // totalSeeds, complete, understanding, repopulating, gate }.
  static async getInterviewState(pillarCode, projectId) {
    const res = await api.get(`${V2}/admin/pillars/${pillarCode}/interview/${projectId}`);
    return unwrap(res);
  }

  // Amend one persisted answer (seed or inferred); locks it as a correction
  // -> { qid, value, source, status, confidence }. Regenerate to re-run.
  static async amendAnswer(pillarCode, projectId, { qid, value } = {}) {
    const res = await api.post(`${V2}/admin/pillars/${pillarCode}/interview/${projectId}/amend`, { qid, value });
    return unwrap(res);
  }

  // Pre-generate AI validation -> { ready, summary, issues[] }. LLM call (5–60s).
  static async validateInterview(pillarCode, projectId) {
    const res = await api.post(`${V2}/admin/pillars/${pillarCode}/interview/${projectId}/validate`, {}, { timeout: 120000 });
    return unwrap(res);
  }

  // Kick off LLM generation for an interviewed project -> { jobId }. Poll
  // getExperimentStatus. Modes:
  //  - full (default): re-infer all hidden answers + regenerate all sections.
  //    Blocked by the readiness gate unless force:true ("Generate anyway").
  //  - sectionsOnly + qids: fast refresh — regenerate only the sections the
  //    amended answers touch, no re-inference.
  static async startInterviewGenerate(pillarCode, projectId, { force = false, sectionsOnly = false, qids = [], idea } = {}) {
    const res = await api.post(`${V2}/admin/pillars/${pillarCode}/interview/${projectId}/generate`, { force, sectionsOnly, qids, ...(idea != null ? { idea } : {}) }, { timeout: 30000 });
    return unwrap(res);
  }

  // Soft override of the detected report type (a lens). Fast (no LLM) -> the
  // fresh interview state (with the new reportType). Regenerate to apply it.
  static async setInterviewReportType(pillarCode, projectId, reportTypeCode) {
    const res = await api.post(`${V2}/admin/pillars/${pillarCode}/interview/${projectId}/report-type`, { reportTypeCode });
    return unwrap(res);
  }

  static async updatePillar(pillarCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/pillars/${pillarCode}`, patch);
    return unwrap(res);
  }

  static async deletePillar(pillarCode) {
    const res = await api.delete(`${V2}/admin/pillars/${pillarCode}`);
    return unwrap(res);
  }

  static async listSubPillars({ pillarCode, all = false, templateCode } = {}) {
    const res = await api.get(`${V2}/sub-pillars`, { params: { pillarCode, ...(all ? { all: 'true' } : {}), ...(templateCode ? { templateCode } : {}) } });
    const data = unwrap(res);
    return Array.isArray(data?.subPillars) ? data.subPillars : [];
  }

  static async getSubPillar(subPillarCode) {
    const res = await api.get(`${V2}/sub-pillars/${subPillarCode}`);
    return unwrap(res);
  }

  static async createSubPillar(payload = {}) {
    const res = await api.post(`${V2}/admin/sub-pillars`, payload);
    return unwrap(res);
  }

  static async updateSubPillar(subPillarCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/sub-pillars/${subPillarCode}`, patch);
    return unwrap(res);
  }

  static async deleteSubPillar(subPillarCode) {
    const res = await api.delete(`${V2}/admin/sub-pillars/${subPillarCode}`);
    return unwrap(res);
  }

  static async listRequirementsCatalog({ subPillarCode, all = false } = {}) {
    const res = await api.get(`${V2}/requirements-catalog`, { params: { subPillarCode, ...(all ? { all: 'true' } : {}) } });
    const data = unwrap(res);
    return Array.isArray(data?.requirements) ? data.requirements : [];
  }

  static async createRequirement(payload = {}) {
    const res = await api.post(`${V2}/admin/requirements-catalog`, payload);
    return unwrap(res);
  }

  static async updateRequirement(requirementCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/requirements-catalog/${requirementCode}`, patch);
    return unwrap(res);
  }

  static async deleteRequirement(requirementCode) {
    const res = await api.delete(`${V2}/admin/requirements-catalog/${requirementCode}`);
    return unwrap(res);
  }

  // ─── Data Management — expert-curated default answers ───────────────
  static async listDefaultAnswers({ requirementCode, pillarCode, subPillarCode, all = false } = {}) {
    const res = await api.get(`${V2}/default-answers`, { params: { requirementCode, pillarCode, subPillarCode, ...(all ? { all: 'true' } : {}) } });
    const data = unwrap(res);
    return Array.isArray(data?.records) ? data.records : [];
  }

  static async defaultAnswerCoverage({ pillarCode } = {}) {
    const res = await api.get(`${V2}/default-answers/coverage`, { params: { pillarCode } });
    return unwrap(res) || {};
  }

  static async createDefaultAnswer(payload = {}) {
    const res = await api.post(`${V2}/admin/default-answers`, payload);
    return unwrap(res);
  }

  static async updateDefaultAnswer(id, patch = {}) {
    const res = await api.patch(`${V2}/admin/default-answers/${id}`, patch);
    return unwrap(res);
  }

  static async deleteDefaultAnswer(id) {
    const res = await api.delete(`${V2}/admin/default-answers/${id}`);
    return unwrap(res);
  }

  // ─── Data Management — building blocks (tech / tools / libraries) ────
  static async listBuildingBlocks({ domain, kind, all = false } = {}) {
    const res = await api.get(`${V2}/building-blocks`, { params: { domain, kind, ...(all ? { all: 'true' } : {}) } });
    const data = unwrap(res);
    return Array.isArray(data?.blocks) ? data.blocks : [];
  }

  static async listBuildingBlockDomains() {
    const res = await api.get(`${V2}/building-blocks/domains`);
    const data = unwrap(res);
    return Array.isArray(data?.domains) ? data.domains : [];
  }

  // ── Knowledge Base coverage audit + enrichment ────────────────────
  // The Coverage audit tab calls all four of these. Their service and
  // controller have existed since the Knowledge Base was built, but no client
  // method did — so the tab threw on mount and took the whole page down with
  // it. Added alongside the routes that expose them.
  //
  // `audit` answers the question the tab exists for: can the report's stack
  // answers be produced entirely from curated blocks, or does it fall through
  // to the model?
  static async auditBuildingBlocks() {
    const res = await api.get(`${V2}/admin/building-blocks/audit`);
    return unwrap(res);
  }

  // Moksy's in-house models: the active one, its last runs, and how many real
  // answers have been recorded to learn from. Retrain is super-admin only.
  static async localModels() {
    const res = await api.get(`${V2}/admin/local-models`);
    return unwrap(res);
  }

  static async trainLocalModel() {
    const res = await api.post(`${V2}/admin/local-models/train`, {});
    return unwrap(res);
  }

  // Bulk import from a JSON export. `mode` is 'merge' | 'replace'.
  static async importBuildingBlocks(blocks, mode = 'merge') {
    const res = await api.post(`${V2}/admin/building-blocks/import`, { blocks, mode });
    return unwrap(res);
  }

  // AI gap-fill for a thin or empty domain. Proposals only — nothing is
  // persisted until an operator accepts them, which is what keeps generated
  // content out of the Knowledge Base by default.
  static async suggestBuildingBlocks({ domain, kind, count = 8, idea = '' } = {}) {
    const res = await api.post(`${V2}/admin/building-blocks/suggest`, { domain, kind, count, idea });
    return unwrap(res);
  }

  // External validation of a domain's curated blocks. Findings only, not
  // applied — same reason as above.
  static async validateBuildingBlocks({ domain, limit = 20 } = {}) {
    const res = await api.post(`${V2}/admin/building-blocks/validate`, { domain, limit });
    return unwrap(res);
  }

  static async createBuildingBlock(payload = {}) {
    const res = await api.post(`${V2}/admin/building-blocks`, payload);
    return unwrap(res);
  }

  static async updateBuildingBlock(id, patch = {}) {
    const res = await api.patch(`${V2}/admin/building-blocks/${id}`, patch);
    return unwrap(res);
  }

  static async deleteBuildingBlock(id) {
    const res = await api.delete(`${V2}/admin/building-blocks/${id}`);
    return unwrap(res);
  }

  static async composeBuildingBlocks({ domain, idea, tags, limit } = {}) {
    const res = await api.post(`${V2}/building-blocks/compose`, { domain, idea, tags, limit }, { timeout: 60000 });
    return unwrap(res) || {};
  }

  // Sub-pillar × factor weights
  static async listSubPillarFactorWeights({ pillarCode } = {}) {
    const res = await api.get(`${V2}/subpillar-factor-weights`, { params: { pillarCode } });
    const data = unwrap(res) || {};
    return { weights: Array.isArray(data.weights) ? data.weights : [], factors: Array.isArray(data.factors) ? data.factors : [] };
  }

  static async saveSubPillarFactorWeights(subPillarCode, { pillarCode, subPillarTitle, weights } = {}) {
    const res = await api.put(`${V2}/admin/subpillar-factor-weights/${subPillarCode}`, { pillarCode, subPillarTitle, weights });
    return unwrap(res);
  }

  static async applySubPillarFactorWeights(pillarCode, { weights } = {}) {
    const res = await api.put(`${V2}/admin/subpillar-factor-weights/apply-all/${pillarCode}`, { weights });
    return unwrap(res);
  }

  // ─── Base template — per-pillar report output/display format ────────
  // Scoped by report type: the default 'solution' returns the generic rows
  // (unchanged); another report type returns its own blocks where authored,
  // else the inherited Solution blocks (flagged `inherited`).
  static async listPillarReportFormats({ reportTypeCode = 'solution' } = {}) {
    const res = await api.get(`${V2}/pillar-report-formats`, { params: { reportTypeCode } });
    const data = unwrap(res) || {};
    return {
      formats: Array.isArray(data.formats) ? data.formats : [],
      reportTypeCode: data.reportTypeCode || reportTypeCode,
      sectionsByPillar: (data.sectionsByPillar && typeof data.sectionsByPillar === 'object') ? data.sectionsByPillar : {},
      displayTypes: Array.isArray(data.displayTypes) ? data.displayTypes : [],
      maxCompositeBlocks: Number(data.maxCompositeBlocks) || 8
    };
  }

  // `compositeBlocks` is the pillar's shape when it is ONE section of a
  // multi-pillar report. Omitted (not sent as []) when the caller has none, so
  // the server leaves any existing set alone rather than wiping it.
  /**
   * One pillar's effective output contract for one report type — its sections
   * with their must-cover (input) and their blocks (output), plus whether that
   * contract is this type's own, inherited from Solution, or derived from its
   * render mode.
   */
  static async getPillarReportFormat(pillarCode, { reportTypeCode = 'solution' } = {}) {
    const res = await api.get(`${V2}/pillar-report-formats/${encodeURIComponent(pillarCode)}`, { params: { reportTypeCode } });
    return unwrap(res);
  }

  static async savePillarReportFormat(pillarCode, { pillarTitle, sections, compositeBlocks, reportTypeCode = 'solution' } = {}) {
    const res = await api.put(`${V2}/admin/pillar-report-formats/${pillarCode}`, {
      pillarTitle, sections, reportTypeCode, ...(compositeBlocks === undefined ? {} : { compositeBlocks })
    });
    return unwrap(res);
  }

  // ─── Patterns — every tunable decision the platform makes ───────────
  /**
   * The function registry — every action the platform exposes, with patterns
   * as the tunable subset. Searched, filtered and paged server-side, because
   * seven hundred rows is past the point where a client can do it.
   */
  /** Which workflow runs each journey — the binding, journey first. */
  static async listJourneyWorkflows(params = {}) {
    const res = await api.get(`${V2}/admin/journey-workflows`, { params });
    return unwrap(res);
  }

  /** Sends only what changed — a template edit must not clear the version. */
  static async bindJourneyWorkflow(code, patch) {
    const res = await api.patch(`${V2}/admin/journey-workflows/${encodeURIComponent(code)}`, patch);
    return unwrap(res);
  }

  /**
   * Every action the platform performs, and what configures it — from the
   * journey map's per-step control record.
   */
  static async listConfigurationIndex(params = {}) {
    const res = await api.get(`${V2}/admin/configuration-index`, { params });
    return unwrap(res);
  }

  // ── Configurations: which workflow a new project runs, and why ────
  static async listWorkflowBindings(params = {}) {
    const res = await api.get(`${V2}/admin/workflow-bindings`, { params });
    return unwrap(res);
  }

  /** What a project with these attributes would run, without starting one. */
  static async dryRunWorkflowBinding(params = {}) {
    const res = await api.get(`${V2}/admin/workflow-bindings/dry-run`, { params });
    return unwrap(res);
  }

  static async createWorkflowBinding(body) {
    const res = await api.post(`${V2}/admin/workflow-bindings`, body);
    return unwrap(res);
  }

  static async updateWorkflowBinding(bindingId, patch) {
    const res = await api.patch(`${V2}/admin/workflow-bindings/${encodeURIComponent(bindingId)}`, patch);
    return unwrap(res);
  }

  static async deleteWorkflowBinding(bindingId) {
    const res = await api.delete(`${V2}/admin/workflow-bindings/${encodeURIComponent(bindingId)}`);
    return unwrap(res);
  }

  // ─── Project fields — declared extensions to the project document ──
  static async listProjectAttributes(params = {}) {
    const res = await api.get(`${V2}/project-attributes`, { params });
    return unwrap(res);
  }

  static async createProjectAttribute(payload) {
    const res = await api.post(`${V2}/admin/project-attributes`, payload);
    return unwrap(res);
  }

  static async updateProjectAttribute(name, patch) {
    const res = await api.patch(`${V2}/admin/project-attributes/${encodeURIComponent(name)}`, patch);
    return unwrap(res);
  }

  static async retireProjectAttribute(name) {
    const res = await api.delete(`${V2}/admin/project-attributes/${encodeURIComponent(name)}`);
    return unwrap(res);
  }

  // ─── Journey templates — named, versioned workflows ────────────────
  //
  // Distinct from the workflow-template calls above: those tune thirteen
  // scalars across one shared step tree, these own the tree.
  static async listJourneyTemplates(params = {}) {
    const res = await api.get(`${V2}/journey-templates`, { params });
    return unwrap(res);
  }

  static async getJourneyTemplate(code) {
    const res = await api.get(`${V2}/journey-templates/${encodeURIComponent(code)}`);
    return unwrap(res);
  }

  /** Every function a step may bind to, for the picker. */
  static async listBindableFunctions() {
    const res = await api.get(`${V2}/journey-templates/bindable`);
    return unwrap(res);
  }

  /** The journeys a new template can be forked from. */
  static async listForkableJourneys() {
    const res = await api.get(`${V2}/journey-templates/forkable`);
    return unwrap(res);
  }

  /** Copy a template under a new code. The copy is always a draft at v1. */
  static async cloneJourneyTemplate(code, payload) {
    const res = await api.post(`${V2}/admin/journey-templates/${encodeURIComponent(code)}/clone`, payload);
    return unwrap(res);
  }

  static async unpublishJourneyTemplate(code) {
    const res = await api.post(`${V2}/admin/journey-templates/${encodeURIComponent(code)}/unpublish`, {});
    return unwrap(res);
  }

  static async getJourneyTemplateRevisions(code) {
    const res = await api.get(`${V2}/journey-templates/${encodeURIComponent(code)}/revisions`);
    return unwrap(res);
  }

  /** Create a template by copying a journey's step tree. */
  static async forkJourneyTemplate(payload) {
    const res = await api.post(`${V2}/admin/journey-templates/fork`, payload);
    return unwrap(res);
  }

  static async updateJourneyTemplate(code, patch) {
    const res = await api.patch(`${V2}/admin/journey-templates/${encodeURIComponent(code)}`, patch);
    return unwrap(res);
  }

  /** Bind a function to a step, or set its parameter values. */
  static async putJourneyTemplateStep(code, stepKey, patch) {
    const res = await api.post(
      `${V2}/admin/journey-templates/${encodeURIComponent(code)}/steps/${encodeURIComponent(stepKey)}`, patch);
    return unwrap(res);
  }

  /** Add a step the source journey does not have. */
  static async addJourneyTemplateStep(code, payload) {
    const res = await api.post(`${V2}/admin/journey-templates/${encodeURIComponent(code)}/steps`, payload);
    return unwrap(res);
  }

  static async removeJourneyTemplateStep(code, stepKey) {
    const res = await api.delete(
      `${V2}/admin/journey-templates/${encodeURIComponent(code)}/steps/${encodeURIComponent(stepKey)}`);
    return unwrap(res);
  }

  /** `keys` is the desired order; anything omitted keeps its relative place. */
  static async reorderJourneyTemplateSteps(code, keys) {
    const res = await api.post(`${V2}/admin/journey-templates/${encodeURIComponent(code)}/steps/reorder`, { keys });
    return unwrap(res);
  }

  /** Every published revision of a journey template, newest first, with codenames. */
  static async journeyTemplateRevisions(code) {
    const res = await api.get(`${V2}/journey-templates/${encodeURIComponent(code)}/revisions`);
    return unwrap(res);
  }

  /** Roll a journey template back to a named revision, as a new minor on the current name. */
  static async rollbackJourneyTemplateTo(code, payload = {}) {
    const res = await api.post(`${V2}/admin/journey-templates/${encodeURIComponent(code)}/rollback-to`, payload);
    return unwrap(res);
  }

  static async publishJourneyTemplate(code, payload = {}) {
    const res = await api.post(`${V2}/admin/journey-templates/${encodeURIComponent(code)}/publish`, payload);
    return unwrap(res);
  }

  static async archiveJourneyTemplate(code) {
    const res = await api.delete(`${V2}/admin/journey-templates/${encodeURIComponent(code)}`);
    return unwrap(res);
  }

  static async listFunctions(params = {}) {
    const res = await api.get(`${V2}/functions`, { params });
    return unwrap(res);
  }

  static async getFunction(functionId) {
    const res = await api.get(`${V2}/functions/${encodeURIComponent(functionId)}`);
    return unwrap(res);
  }

  /** Curate one function. Only what a route cannot tell you is writable. */
  static async updateFunction(functionId, patch) {
    const res = await api.patch(`${V2}/admin/functions/${encodeURIComponent(functionId)}`, patch);
    return unwrap(res);
  }

  static async listPatterns(params = {}) {
    const res = await api.get(`${V2}/patterns`, { params });
    return unwrap(res);
  }

  // ── the rows a data-driven pattern decides from ──────────────────
  // Behaviour, not configuration: editing one changes what the pattern decides
  // on the next run. That is why the editor sits beside the bench.
  static async listPatternTable(patternId) {
    const res = await api.get(`${V2}/patterns/${encodeURIComponent(patternId)}/table`);
    const data = unwrap(res);
    return Array.isArray(data?.rows) ? data.rows : [];
  }

  static async savePatternTableRow(patternId, row) {
    const res = await api.put(`${V2}/admin/patterns/${encodeURIComponent(patternId)}/table`, row);
    return unwrap(res);
  }

  static async seedPatternTable(patternId) {
    const res = await api.post(`${V2}/admin/patterns/${encodeURIComponent(patternId)}/table/seed`, {});
    return unwrap(res);
  }

  static async deletePatternTableRow(patternId, key) {
    const res = await api.delete(`${V2}/admin/patterns/${encodeURIComponent(patternId)}/table/${encodeURIComponent(key)}`);
    return unwrap(res);
  }

  static async createPattern(payload) {
    const res = await api.post(`${V2}/admin/patterns`, payload);
    return unwrap(res);
  }

  static async updatePattern(patternId, patch) {
    const res = await api.patch(`${V2}/admin/patterns/${encodeURIComponent(patternId)}`, patch);
    return unwrap(res);
  }

  static async deletePattern(patternId) {
    const res = await api.delete(`${V2}/admin/patterns/${encodeURIComponent(patternId)}`);
    return unwrap(res);
  }

  static async getPattern(patternId) {
    const res = await api.get(`${V2}/patterns/${encodeURIComponent(patternId)}`);
    return unwrap(res);
  }

  static async draftPatternRevision(patternId, payload) {
    const res = await api.post(`${V2}/admin/patterns/${encodeURIComponent(patternId)}/revisions`, payload);
    return unwrap(res);
  }

  static async publishPatternRevision(patternId, number) {
    const res = await api.post(`${V2}/admin/patterns/${encodeURIComponent(patternId)}/revisions/${number}/publish`, {});
    return unwrap(res);
  }

  /** The bench. Stores nothing — the response is the result. */
  static async testPattern(patternId, payload) {
    const res = await api.post(`${V2}/admin/patterns/${encodeURIComponent(patternId)}/test`, payload);
    return unwrap(res);
  }

  // ─── Report types — lenses over the shared pillar tree ──────────────
  static async listReportTypes({ all = false, templateCode } = {}) {
    const res = await api.get(`${V2}/report-types`, { params: { ...(all ? { all: 'true' } : {}), ...(templateCode ? { templateCode } : {}) } });
    const data = unwrap(res);
    return Array.isArray(data?.reportTypes) ? data.reportTypes : [];
  }

  static async getReportTypeComposition(reportTypeCode) {
    const res = await api.get(`${V2}/report-types/${reportTypeCode}/composition`);
    return unwrap(res);
  }

  static async seedReportTypes() {
    const res = await api.post(`${V2}/admin/report-types/seed`, {});
    return unwrap(res);
  }

  static async createReportType(payload = {}) {
    const res = await api.post(`${V2}/admin/report-types`, payload);
    return unwrap(res);
  }

  static async updateReportType(reportTypeCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/report-types/${reportTypeCode}`, patch);
    return unwrap(res);
  }

  static async setReportTypeMember(reportTypeCode, member = {}) {
    const res = await api.patch(`${V2}/admin/report-types/${reportTypeCode}/members`, member);
    return unwrap(res);
  }

  static async deleteReportType(reportTypeCode) {
    const res = await api.delete(`${V2}/admin/report-types/${reportTypeCode}`);
    return unwrap(res);
  }

  static async listMasterTemplates({ all = false } = {}) {
    const res = await api.get(`${V2}/master-templates`, all ? { params: { all: 'true' } } : {});
    const data = unwrap(res);
    return Array.isArray(data?.templates) ? data.templates : [];
  }

  static async resolveMasterTemplate(templateCode) {
    const res = await api.get(`${V2}/master-templates/${templateCode}/resolve`);
    return unwrap(res);
  }

  static async createMasterTemplate(payload = {}) {
    const res = await api.post(`${V2}/admin/master-templates`, payload);
    return unwrap(res);
  }

  static async updateMasterTemplate(templateCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/master-templates/${templateCode}`, patch);
    return unwrap(res);
  }

  static async listDocumentCategoryTemplates({ all = false } = {}) {
    const res = await api.get(`${V2}/document-category-templates`, all ? { params: { all: 'true' } } : {});
    const data = unwrap(res);
    return Array.isArray(data?.categories) ? data.categories : [];
  }

  static async resolveDocumentCategoryTemplate(categoryCode) {
    const res = await api.get(`${V2}/document-category-templates/${categoryCode}/resolve`);
    return unwrap(res);
  }

  static async cloneCategoryTemplateFromMaster(payload = {}) {
    const res = await api.post(`${V2}/admin/document-category-templates/clone-from-master`, payload);
    return unwrap(res);
  }

  static async updateDocumentCategoryTemplate(categoryCode, patch = {}) {
    const res = await api.patch(`${V2}/admin/document-category-templates/${categoryCode}`, patch);
    return unwrap(res);
  }

  static async deleteDocumentCategoryTemplate(categoryCode) {
    const res = await api.delete(`${V2}/admin/document-category-templates/${categoryCode}`);
    return unwrap(res);
  }

  static async classifyDocumentCategory(idea) {
    const res = await api.post(`${V2}/document-category-templates/classify`, { idea });
    return unwrap(res);
  }

  // ─── Template proposal pipeline (category -> master -> base) ───────
  // Submit a proposal. Gated by capability at L7+ in the track, checked
  // server-side — the screen explains the gate rather than hiding the form,
  // because a reviewer who cannot yet propose should know what they are
  // working towards.
  static async proposeTemplateChange(payload = {}) {
    const res = await api.post(`${V2}/template-proposals`, payload);
    return unwrap(res);
  }
  static async listTemplateProposals({ tier, categoryCode, status } = {}) {
    const res = await api.get(`${V2}/template-proposals`, { params: { tier, categoryCode, status } });
    const data = unwrap(res);
    return Array.isArray(data?.proposals) ? data.proposals : [];
  }

  static async decideCategoryTierProposal(proposalId, payload = {}) {
    const res = await api.post(`${V2}/template-proposals/${proposalId}/category-decision`, payload);
    return unwrap(res);
  }

  static async decideMasterTierProposal(proposalId, payload = {}) {
    const res = await api.post(`${V2}/template-proposals/${proposalId}/master-decision`, payload);
    return unwrap(res);
  }

  static async decideBaseTierProposal(proposalId, payload = {}) {
    const res = await api.post(`${V2}/template-proposals/${proposalId}/base-decision`, payload);
    return unwrap(res);
  }

  // The one MOX↔money rate, and the admin setter. Standard 1 MOX = $0.01;
  // set here it applies to every price and label on the platform.
  static async getMoneyConfig() {
    const res = await api.get(`${V2}/money-config`);
    return unwrap(res);
  }
  static async setMoneyConfig(patch = {}) {
    const res = await api.patch(`${V2}/admin/money-config`, patch);
    return unwrap(res);
  }

  // Finance rollup: every reviewer with money in either ledger.
  static async adminEarningsSummary() {
    const res = await api.get(`${V2}/admin/earnings/summary`);
    return unwrap(res);
  }

  // The requirements-service MOX ledger — the wallet that review escrow,
  // tips, gifts and reviewer earnings actually move through.
  // Withdraw EARNED MOX from the ledger (KYC-gated in requirements-service;
  // refuses purchased/reward credit by name). This replaced the payments
  // cashout that drew from the subscriptions counter.
  static async withdrawFromLedger({ amount, payoutMethod = 'bank_uk', payoutAccountId = '', payoutRef = '', note = '' } = {}) {
    const res = await api.post(`${V2}/me/wallet/withdraw`, { amount, payoutMethod, payoutAccountId, payoutRef, note });
    return res.data?.data ?? res.data;
  }
  // 🏛️ The Commons window (ownership plan §3): public by class, my window, my units, the admin steps.
  static async commonsPublic() { const res = await api.get(`${V2}/commons`); return res.data?.data ?? res.data; }
  static async commonsMyWindow(params = {}) { const res = await api.get(`${V2}/commons/window`, { params }); return res.data?.data ?? res.data; }
  static async commonsMe() { const res = await api.get(`${V2}/commons/me`); return res.data?.data ?? res.data; }
  static async commonsCertificate(certificateId) { const res = await api.get(`${V2}/commons/certificates/${encodeURIComponent(certificateId)}`); return res.data?.data ?? res.data; }
  static async commonsCommit(windowId, { mox = 0, mxp = 0 } = {}) { const res = await api.post(`${V2}/commons/windows/${encodeURIComponent(windowId)}/commit`, { mox, mxp }); return res.data?.data ?? res.data; }
  static async commonsWithdraw(windowId) { const res = await api.post(`${V2}/commons/windows/${encodeURIComponent(windowId)}/withdraw`, {}); return res.data?.data ?? res.data; }
  static async adminCommonsWindows() { const res = await api.get(`${V2}/admin/commons/windows`); return res.data?.data ?? res.data; }
  static async adminCommonsWindow(windowId) { const res = await api.get(`${V2}/admin/commons/windows/${encodeURIComponent(windowId)}`); return res.data?.data ?? res.data; }
  static async adminCommonsSchedule(body) { const res = await api.post(`${V2}/admin/commons/windows`, body); return res.data?.data ?? res.data; }
  static async commonsMoney() { const res = await api.get(`${V2}/commons/money`); return res.data?.data ?? res.data; }
  static async commonsDividendElection(election) { const res = await api.post(`${V2}/commons/dividend-election`, { election }); return res.data?.data ?? res.data; }
  static async commonsOfferBuyback(buybackId, units) { const res = await api.post(`${V2}/commons/buybacks/${encodeURIComponent(buybackId)}/offer`, { units }); return res.data?.data ?? res.data; }
  static async adminCommonsDividend(body) { const res = await api.post(`${V2}/admin/commons/dividends`, body); return res.data?.data ?? res.data; }
  static async adminCommonsOpenBuyback(body) { const res = await api.post(`${V2}/admin/commons/buybacks`, body); return res.data?.data ?? res.data; }
  static async adminCommonsCloseBuyback(buybackId) { const res = await api.post(`${V2}/admin/commons/buybacks/${encodeURIComponent(buybackId)}/close`, { force: true }); return res.data?.data ?? res.data; }
  static async adminCommonsRound(body) { const res = await api.post(`${V2}/admin/commons/rounds`, body); return res.data?.data ?? res.data; }
  static async adminCommonsLiquidityDryRun(body) { const res = await api.post(`${V2}/admin/commons/liquidity-dry-runs`, body); return res.data?.data ?? res.data; }
  static async commonsIdentity() { const res = await api.get(`${V2}/commons/identity`); return res.data?.data ?? res.data; }
  static async commonsIdentitySubmit(body) { const res = await api.post(`${V2}/commons/identity`, body); return res.data?.data ?? res.data; }
  static async adminCommonsIdentities(state = 'pending') { const res = await api.get(`${V2}/admin/commons/identities`, { params: { state } }); const d = res.data?.data ?? res.data; return Array.isArray(d?.items) ? d.items : []; }
  static async adminCommonsIdentityDecide(userId, body) { const res = await api.post(`${V2}/admin/commons/identities/${encodeURIComponent(userId)}/decide`, body); return res.data?.data ?? res.data; }
  static async commonsGovernance() { const res = await api.get(`${V2}/commons/governance`); return res.data?.data ?? res.data; }
  static async commonsPropose(body) { const res = await api.post(`${V2}/commons/proposals`, body); return res.data?.data ?? res.data; }
  static async commonsVoteProposal(proposalId, choice) { const res = await api.post(`${V2}/commons/proposals/${encodeURIComponent(proposalId)}/vote`, { choice }); return res.data?.data ?? res.data; }
  static async commonsStand(electionId, statement = '') { const res = await api.post(`${V2}/commons/elections/${encodeURIComponent(electionId)}/stand`, { statement }); return res.data?.data ?? res.data; }
  static async commonsVoteElection(electionId, candidateId) { const res = await api.post(`${V2}/commons/elections/${encodeURIComponent(electionId)}/vote`, { candidateId }); return res.data?.data ?? res.data; }
  static async adminCommonsCloseProposal(proposalId, body = {}) { const res = await api.post(`${V2}/admin/commons/proposals/${encodeURIComponent(proposalId)}/close`, body); return res.data?.data ?? res.data; }
  static async adminCommonsScheduleElection(body) { const res = await api.post(`${V2}/admin/commons/elections`, body); return res.data?.data ?? res.data; }
  static async adminCommonsCloseElection(electionId, body = {}) { const res = await api.post(`${V2}/admin/commons/elections/${encodeURIComponent(electionId)}/close`, body); return res.data?.data ?? res.data; }
  static async adminCommonsGrants() { const res = await api.get(`${V2}/admin/commons/options/grants`); const d = res.data?.data ?? res.data; return Array.isArray(d?.items) ? d.items : []; }
  static async adminCommonsProposeGrant(body) { const res = await api.post(`${V2}/admin/commons/options/grants`, body); return res.data?.data ?? res.data; }
  static async adminCommonsSignGrant(grantId) { const res = await api.post(`${V2}/admin/commons/options/grants/${encodeURIComponent(grantId)}/sign`, {}); return res.data?.data ?? res.data; }
  static async adminCommonsReport(quarter) { const res = await api.post(`${V2}/admin/commons/report`, { quarter }); return res.data?.data ?? res.data; }
  static async adminCommonsStep(windowId, step, body = {}) { const res = await api.post(`${V2}/admin/commons/windows/${encodeURIComponent(windowId)}/${step}`, body); return res.data?.data ?? res.data; }
  // 🏦 The monthly pool: this month so far, my verified MXP, an estimated share.
  static async poolCurrent() {
    const res = await api.get(`${V2}/pool/current`);
    return res.data?.data ?? res.data;
  }
  static async poolStatements() { const res = await api.get(`${V2}/pool/statements`); const d = res.data?.data ?? res.data; return Array.isArray(d?.items) ? d.items : []; }
  static async adminPendingWithdrawals() { const res = await api.get(`${V2}/admin/wallet/withdrawals`); const d = res.data?.data ?? res.data; return Array.isArray(d?.items) ? d.items : []; }
  // 🛑 Account holds (the anomaly rule): list, place, release.
  static async adminAccountHolds(status = 'active') { const res = await api.get(`${V2}/admin/wallet/account-holds`, { params: { status } }); const d = res.data?.data ?? res.data; return Array.isArray(d?.items) ? d.items : []; }
  static async adminPlaceAccountHold(body = {}) { const res = await api.post(`${V2}/admin/wallet/account-holds`, body); return res.data?.data ?? res.data; }
  static async adminReleaseAccountHold(holdId, body = {}) { const res = await api.post(`${V2}/admin/wallet/account-holds/${encodeURIComponent(holdId)}/release`, body); return res.data?.data ?? res.data; }
  static async adminHeldEarnings() { const res = await api.get(`${V2}/admin/wallet/holds`); const d = res.data?.data ?? res.data; return Array.isArray(d?.items) ? d.items : []; }
  static async adminDecideHold(entryId, body = {}) { const res = await api.post(`${V2}/admin/wallet/holds/${encodeURIComponent(entryId)}/decide`, body); return res.data?.data ?? res.data; }
  static async adminSettleWithdrawal(entryId, body = {}) { const res = await api.post(`${V2}/admin/wallet/withdrawals/${encodeURIComponent(entryId)}/settle`, body); return res.data?.data ?? res.data; }
  static async poolPeriods() { const res = await api.get(`${V2}/admin/pool/periods`); return res.data?.data ?? res.data; }
  static async poolPreview(period, body = {}) { const res = await api.post(`${V2}/admin/pool/periods/${encodeURIComponent(period)}/preview`, body); return res.data?.data ?? res.data; }
  static async poolClose(period, body = {}) { const res = await api.post(`${V2}/admin/pool/periods/${encodeURIComponent(period)}/close`, body); return res.data?.data ?? res.data; }
  static async myLedgerWithdrawals({ limit = 10 } = {}) {
    const res = await api.get(`${V2}/me/wallet/withdrawals`, { params: { limit } });
    const items = res.data?.data?.items ?? res.data?.data ?? res.data ?? [];
    return { items: Array.isArray(items) ? items : [] };
  }
  // What moved the wallet, newest first — every credit and debit with its reason.
  static async myLedgerActivity({ limit = 30 } = {}) {
    const res = await api.get(`${V2}/me/wallet/activity`, { params: { limit } });
    const items = res.data?.data?.items ?? res.data?.data ?? res.data ?? [];
    return { items: Array.isArray(items) ? items : [] };
  }
  static async myLedgerWallet() {
    const res = await api.get(`${V2}/wallet`);
    return unwrap(res);
  }

  // ── Direct invitation, the recipient's side ─────────────────────────
  // `open` is what the emailed magic link lands on: a guest session is
  // enough, and it returns only what the email already said. `accept`
  // needs a signed-in member whose email is the one the invitation went
  // to — the server refuses anyone else, so this deliberately does NOT
  // fall back to a guest token.
  static async openInvitation(token) {
    const res = await guestRetry(async () =>
      api.get(`${V2}/invitations/open/${encodeURIComponent(token)}`, await withGuestAuth())
    );
    return unwrap(res);
  }

  static async acceptInvitation(token, payload = {}) {
    const res = await api.post(`${V2}/invitations/accept/${encodeURIComponent(token)}`, payload);
    return unwrap(res);
  }
}

export default RequirementsV2API;
